import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Plus, X, Download, FileText, Trash2, Edit2, Eye, Sparkles, Loader } from 'lucide-react';
import { NEBB_LOGO } from '../assets_logo';
import { logoDe } from '../assets_logos';

const ZONAS = ['Montevideo', 'Interior', 'Todo el país'];

// Seguimiento comercial de la cotización (qué pasó con el cliente).
export const SEGUIMIENTO = [
  { id: 'sin_contactar', label: 'Sin contactar', color: '#8b8fa3' },
  { id: 'contactado', label: 'Contactado · esperando respuesta', color: '#5b8def' },
  { id: 'sin_respuesta', label: 'Sin respuesta', color: '#d9a23b' },
  { id: 'volver_contactar', label: 'Lo está pensando · volver a contactar', color: '#d9a23b' },
  { id: 'quiere_emitir', label: 'Quiere emitir · faltan días', color: '#14b8a6' },
  { id: 'solo_cotizar', label: 'Solo quería cotizar', color: '#8b8fa3' },
  { id: 'caro', label: 'Le pareció caro', color: '#e07a5f' },
  { id: 'no_interesa', label: 'No le interesa emitir', color: '#e04b4b' },
  { id: 'ya_tiene', label: 'Ya tiene seguro / emitió con otro', color: '#e04b4b' },
  { id: 'datos_erroneos', label: 'Datos erróneos / no se pudo contactar', color: '#e04b4b' },
  { id: 'emitio', label: 'Emitió con NEBB ✓', color: '#2fb67c' },
];
const segDe = (id) => SEGUIMIENTO.find(s => s.id === id) || SEGUIMIENTO[0];
const CANALES = ['WhatsApp', 'Llamada', 'Mail', 'En persona'];

// ── Grupos de la lista: abiertas / emitidas / perdidas ────────
// Las emitidas y las perdidas salen de la vista del día a día, pero no se
// borran: quedan en su pestaña y cuentan para los números.
const PERDIDOS = ['solo_cotizar', 'caro', 'no_interesa', 'ya_tiene', 'datos_erroneos'];
const CERRADOS = ['emitio', ...PERDIDOS];
const GRUPOS = [
  { id: 'abiertas', label: 'Abiertas' },
  { id: 'emitidas', label: 'Emitidas' },
  { id: 'perdidas', label: 'Perdidas' },
  { id: 'todas', label: 'Todas' },
];
// Las solicitudes de la web (hogar, bici, microseguros…) se manejan en la
// bandeja de Ventas web: su estado sale de ahí, no del seguimiento.
const RAMOS_WEB = ['hogar', 'monopatin', 'garantia', 'bici', 'micro'];
const estadoWeb = (q) => (q && RAMOS_WEB.includes(q.ramo) && q[q.ramo] && typeof q[q.ramo] === 'object')
  ? (q[q.ramo].estado || (q.ramo === 'garantia' ? 'pedir-documentos' : 'para-emitir')) : null;
// La asistencia en viaje pagada (web o transferencia) se maneja en la
// bandeja de Emisión: su estado sale de ahí.
const estadoEmision = (q) => (q && q.emision) ? ((q.emision.pago && q.emision.pago.estado) || 'esperando-transferencia') : null;
export function grupoDe(q) {
  const ee = estadoEmision(q);
  if (ee) return ee === 'emitida' ? 'emitidas' : (ee === 'cancelada' ? 'perdidas' : 'abiertas');
  const ew = estadoWeb(q);
  if (ew) return ['pagada', 'emitida'].includes(ew) ? 'emitidas' : (['cancelada', 'rechazada'].includes(ew) ? 'perdidas' : 'abiertas');
  const s = q.seguimiento || 'sin_contactar';
  if (s === 'emitio') return 'emitidas';
  if (PERDIDOS.includes(s)) return 'perdidas';
  return 'abiertas';
}
const ms = (iso) => { const t = Date.parse(iso || ''); return Number.isFinite(t) ? t : 0; };
// Última vez que pasó algo con la cotización (alta, contacto, cambio en la bandeja).
function ultimoMovimiento(q) {
  const ew = estadoWeb(q);
  const hist = ew ? (q[q.ramo].historial || []) : [];
  return Math.max(ms(q.createdAt), ...(q.seguimientoLog || []).map(e => ms(e.fecha)), ...hist.map(h => ms(h.at)));
}
// Cuándo se cerró (emitida o perdida): para contarla en el mes que corresponde.
function fechaCierre(q) {
  if (q.cerradaEn) return ms(q.cerradaEn);
  const log = (q.seguimientoLog || []).find(e => CERRADOS.includes(e.estado));
  if (log) return ms(log.fecha);
  return ultimoMovimiento(q);
}
const DIAS_VENCIDA = 30;
const esVencida = (q) => grupoDe(q) === 'abiertas' && !estadoWeb(q) && !estadoEmision(q) && (Date.now() - ultimoMovimiento(q)) > DIAS_VENCIDA * 864e5;
// Con qué compañía emitió (si se cargó), para saber cuál elige la gente.
const companiaEmitida = (q) => q.emitioCon || ((estadoWeb(q) || estadoEmision(q)) && (q.prices || [])[0] && q.prices[0].company) || '';
const RAMO_LABEL = { auto: 'Auto', hogar: 'Hogar', monopatin: 'Monopatín', garantia: 'Garantía', bici: 'Bici', micro: 'Microseguros', mascotas: 'Mascotas', asistencia: 'Asistencia en viaje' };
const ramoDe = (q) => q.ramo || 'auto';
const mesDe = (t) => { const d = new Date(t); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const MESES_NOMBRE = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (m) => { const [a, n] = m.split('-'); return `${MESES_NOMBRE[Number(n) - 1]} ${a}`; };

// Número para WhatsApp (Uruguay por defecto).
function numeroWA(tel) {
  let d = String(tel || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('598')) return d.length >= 11 ? d : '';
  if (d.startsWith('0')) d = d.slice(1);
  if (d.length === 8) return '598' + d;
  return d.length >= 10 ? d : '';
}
const linkWA = (tel, texto) => {
  const n = numeroWA(tel);
  const url = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? `https://wa.me/${n}` : `https://web.whatsapp.com/send?phone=${n}`;
  return texto ? url + (url.includes('?') ? '&' : '?') + 'text=' + encodeURIComponent(texto) : url;
};
// Link a la cotización que ve el cliente: siempre con el dominio propio
// (salvo en desarrollo), aunque el panel se abra desde otro.
const linkCotizacion = (codigo) => {
  if (!codigo) return '';
  const base = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) ? window.location.origin : 'https://crm.nebbrokers.com';
  return `${base}/c/${codigo}`;
};
// "RICHARD fernando" → "Richard": solo el primer nombre, bien escrito.
const primerNombre = (n) => {
  const x = String(n || '').trim().split(/\s+/)[0] || '';
  return x ? x.charAt(0).toUpperCase() + x.slice(1).toLowerCase() : '';
};
// Mensaje de seguimiento que se abre al tocar el botón de WhatsApp de una
// cotización. Lo revisás y lo mandás (WhatsApp no lo envía solo).
function mensajeSeguimiento(q) {
  const nombre = primerNombre(q.clientName);
  const vehiculo = [q.marca, q.modelo, q.anio].filter(Boolean).join(' ');
  const link = linkCotizacion(q.codigo);
  return `¡Hola${nombre ? ' ' + nombre : ''}! ¿Cómo estás? Te escribimos de NEBB Brokers. ` +
    `Vimos que cotizaste el seguro de tu ${vehiculo || 'vehículo'} con nosotros y queríamos saber si pudiste ver las opciones.` +
    (link ? `\n\nTe dejamos de nuevo tu cotización con todas las compañías:\n${link}` : '') +
    `\n\nCualquier consulta, respondé por acá y te ayudamos a elegir. ¡Quedamos atentos!`;
}
const fechaHora = (iso) => { try { return format(new Date(iso), 'dd/MM/yyyy HH:mm'); } catch { return ''; } };

// ── Emisión a futuro: responsable, documentos y recordatorios ──
// Caso típico: el cliente quiere emitir (ya tenemos la carta de 0 km) pero la
// emisión es dentro de unos días. Se marca la fecha y los días en que el
// sistema manda el recordatorio por mail al responsable.
const TIPOS_DOC = ['Carta de 0 km', 'Cédula', 'Libreta de propiedad', 'Comprobante de domicilio', 'Otro'];
const RECORDAR_ANTES = [
  { d: 0, label: 'El mismo día' }, { d: 1, label: '1 día antes' },
  { d: 3, label: '3 días antes' }, { d: 7, label: '1 semana antes' },
];
const sumarDiasISO = (iso, n) => new Date(Date.parse(iso + 'T12:00:00Z') + n * 864e5).toISOString().slice(0, 10);
const hoyISO = () => format(new Date(), 'yyyy-MM-dd');
const fechaCorta = (iso) => String(iso || '').split('-').reverse().join('/');
const kbDe = (n) => n > 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';
async function llamarDocs(metodo, ruta, opts = {}) {
  const token = localStorage.getItem('nebb_token');
  const res = await fetch(`/api/quotes/${ruta}`, {
    method: metodo, body: opts.body,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.tipo ? { 'Content-Type': opts.tipo } : {}) },
  });
  if (!res.ok) {
    let msg = '';
    try { msg = (await res.json()).error; } catch { /* sin cuerpo */ }
    throw new Error(msg || `Error ${res.status}`);
  }
  return opts.blob ? res.blob() : res.json();
}

function EmisionFutura({ form, set }) {
  const { users, showToast } = useApp();
  const [tipoDoc, setTipoDoc] = useState(TIPOS_DOC[0]);
  const [subiendo, setSubiendo] = useState(false);
  const [otraFecha, setOtraFecha] = useState('');
  const inputRef = useRef();
  const docs = form.docsEmision || [];
  const plan = form.emisionPlan || { fecha: '', recordar: [] };
  const recordar = plan.recordar || [];
  const hoy = hoyISO();
  const equipo = (users || []).filter(u => u.role !== 'vsa');

  const guardarPlan = (cambios) => set('emisionPlan', { ...plan, recordar, ...cambios });
  // Si se mueve la fecha de emisión, los recordatorios "N días antes" que todavía
  // no salieron se mueven con ella; los de fecha fija se quedan donde están.
  const cambiarFecha = (f) => guardarPlan({
    fecha: f,
    recordar: recordar.map(r => (typeof r.antes === 'number' && !r.enviado && f) ? { ...r, fecha: sumarDiasISO(f, -r.antes) } : r),
  });
  const alternar = (d) => {
    if (!plan.fecha) return;
    const fecha = sumarDiasISO(plan.fecha, -d);
    const ya = recordar.find(r => r.antes === d);
    guardarPlan({ recordar: ya ? recordar.filter(r => r !== ya) : [...recordar, { fecha, antes: d }].sort((a, b) => a.fecha.localeCompare(b.fecha)) });
  };
  const agregarFija = () => {
    if (!otraFecha || recordar.some(r => r.fecha === otraFecha)) { setOtraFecha(''); return; }
    guardarPlan({ recordar: [...recordar, { fecha: otraFecha }].sort((a, b) => a.fecha.localeCompare(b.fecha)) });
    setOtraFecha('');
  };

  const subir = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setSubiendo(true);
    try {
      const nombre = `${tipoDoc}${tipoDoc === 'Otro' ? ' · ' + f.name.replace(/\.[^.]+$/, '') : ''}`;
      const r = await llamarDocs('POST', `${form.id}/docs?nombre=${encodeURIComponent(nombre)}&tipo=${encodeURIComponent(tipoDoc)}`, { body: f, tipo: f.type || 'application/octet-stream' });
      set('docsEmision', r.docsEmision || []);
      if (showToast) showToast('Documento cargado', 'success');
    } catch (err) {
      if (showToast) showToast(err.message || 'No se pudo subir el documento', 'error'); else alert(err.message);
    } finally { setSubiendo(false); }
  };
  const ver = async (d) => {
    const w = window.open('', '_blank');
    try {
      const blob = await llamarDocs('GET', `${form.id}/docs/${d.id}`, { blob: true });
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (err) { if (w) w.close(); if (showToast) showToast(err.message, 'error'); }
  };
  const borrar = async (d) => {
    if (!window.confirm(`¿Borrar "${d.nombre}"?`)) return;
    try {
      const r = await llamarDocs('DELETE', `${form.id}/docs/${d.id}`);
      set('docsEmision', r.docsEmision || []);
    } catch (err) { if (showToast) showToast(err.message, 'error'); }
  };

  const caja = { border: '1px solid var(--border)', borderRadius: 12, padding: 16 };
  const chipBtn = (on, off) => ({ fontFamily: 'inherit', fontSize: 12.5, cursor: off ? 'not-allowed' : 'pointer', borderRadius: 8, padding: '6px 12px', opacity: off ? 0.45 : 1,
    border: `1px solid ${on ? '#14b8a6' : 'var(--border)'}`, background: on ? '#14b8a626' : 'transparent', color: on ? '#14b8a6' : 'var(--text-secondary)', fontWeight: on ? 700 : 500 });
  return (
    <>
      {/* Responsable */}
      <div className="form-group" style={{ maxWidth: 320, margin: 0 }}>
        <label className="form-label">Responsable de la emisión</label>
        <select value={form.asignadoA || ''} onChange={e => set('asignadoA', e.target.value)}>
          <option value="">Sin asignar</option>
          {equipo.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      </div>

      {/* Fecha y recordatorios (solo si quiere emitir más adelante) */}
      {form.seguimiento === 'quiere_emitir' && (
        <div style={caja}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Emisión a futuro</div>
          <div className="form-group" style={{ maxWidth: 260 }}>
            <label className="form-label">Fecha prevista de emisión</label>
            <input type="date" min={hoy} value={plan.fecha || ''} onChange={e => cambiarFecha(e.target.value)} />
          </div>
          <label className="form-label" style={{ marginTop: 8 }}>Recordarme (te llega un mail a las 9 h)</label>
          {!plan.fecha && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 8px' }}>Elegí primero la fecha de emisión.</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 10px' }}>
            {RECORDAR_ANTES.map(o => {
              const fecha = plan.fecha ? sumarDiasISO(plan.fecha, -o.d) : '';
              const on = recordar.some(r => r.antes === o.d);
              const pasado = !!fecha && fecha < hoy;
              return <button key={o.d} type="button" disabled={!plan.fecha || (pasado && !on)} onClick={() => alternar(o.d)} title={fecha ? fechaCorta(fecha) : ''} style={chipBtn(on, !plan.fecha || (pasado && !on))}>{o.label}{fecha ? ` · ${fechaCorta(fecha)}` : ''}</button>;
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="date" min={hoy} value={otraFecha} onChange={e => setOtraFecha(e.target.value)} style={{ maxWidth: 170 }} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={agregarFija} disabled={!otraFecha}>+ Otro día</button>
          </div>
          {recordar.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              {recordar.map((r, i) => (
                <div key={r.fecha + i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 10px', background: 'var(--bg-input)', borderRadius: 8 }}>
                  <span>🔔 {fechaCorta(r.fecha)}{r.enviado ? ' · ya enviado ✓' : r.fecha < hoy ? ' · vencido sin enviar' : ''}</span>
                  <button type="button" onClick={() => guardarPlan({ recordar: recordar.filter(x => x !== r) })} title="Quitar" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 15 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Documentos */}
      <div style={caja}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Documentos {docs.length ? `(${docs.length})` : ''}</div>
        {!form.id ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Guardá la cotización para poder cargar documentos.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={tipoDoc} onChange={e => setTipoDoc(e.target.value)} style={{ maxWidth: 230 }}>
                {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,application/pdf,image/*" onChange={subir} style={{ display: 'none' }} />
              <button type="button" className="btn btn-secondary btn-sm" disabled={subiendo} onClick={() => inputRef.current && inputRef.current.click()}>
                {subiendo ? 'Subiendo…' : '+ Cargar documento'}
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>PDF, foto o Word · hasta 15 MB</span>
            </div>
            {docs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                {docs.map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {d.nombre}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{kbDe(d.bytes)} · {fechaHora(d.at)}{d.por ? ` · ${d.por}` : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => ver(d)}>Ver</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => borrar(d)} title="Borrar">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Seguimiento({ form, set, usuario }) {
  const [canal, setCanal] = useState('WhatsApp');
  const [nota, setNota] = useState('');
  const [nuevoEstado, setNuevoEstado] = useState('');
  const log = form.seguimientoLog || [];
  const estado = segDe(form.seguimiento);
  const tel = form.clientPhone || '', mail = form.clientEmail || '';
  // Al cerrarla (emitida o perdida) queda la fecha, para los números del mes.
  const cambiarEstado = (id) => {
    set('seguimiento', id);
    set('cerradaEn', CERRADOS.includes(id) ? (CERRADOS.includes(form.seguimiento) && form.cerradaEn ? form.cerradaEn : new Date().toISOString()) : '');
  };
  const companiasCotizadas = [...new Set((form.prices || []).map(p => p.company).filter(Boolean))];

  const agregar = () => {
    const est = nuevoEstado || (form.seguimiento && form.seguimiento !== 'sin_contactar' ? form.seguimiento : 'contactado');
    if (!nota.trim() && est === form.seguimiento) return;
    const entrada = { fecha: new Date().toISOString(), canal, nota: nota.trim(), estado: est, autor: usuario || '' };
    set('seguimientoLog', [entrada, ...log]);
    cambiarEstado(est);
    setNota(''); setNuevoEstado('');
  };

  const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, borderRadius: 999, padding: '6px 12px', textDecoration: 'none' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Cliente y contacto rápido */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 16px', background: 'var(--bg-input)', borderRadius: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{form.clientName || 'Sin nombre'}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
            {[[form.marca, form.modelo, form.anio].filter(Boolean).join(' '), tel, mail].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {numeroWA(tel) && <a href={linkWA(tel, mensajeSeguimiento(form))} target="_blank" rel="noopener noreferrer" title="Abre WhatsApp con el mensaje de seguimiento" style={{ ...chip, background: '#25d366', color: '#fff' }}>WhatsApp</a>}
          {tel && <a href={`tel:${tel}`} style={{ ...chip, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>Llamar</a>}
          {mail && <a href={`mailto:${mail}`} style={{ ...chip, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>Mail</a>}
          {form.codigo && <a href={`/c/${form.codigo}`} target="_blank" rel="noopener noreferrer" style={{ ...chip, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>Lo que ve el cliente</a>}
        </div>
      </div>

      {/* Estado actual */}
      <div>
        <label className="form-label">Estado del cliente</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {SEGUIMIENTO.map(op => {
            const activo = (form.seguimiento || 'sin_contactar') === op.id;
            return (
              <button key={op.id} type="button" onClick={() => cambiarEstado(op.id)}
                style={{ fontFamily: 'inherit', fontSize: 12.5, fontWeight: activo ? 700 : 500, cursor: 'pointer', borderRadius: 999, padding: '7px 13px',
                  border: `1.5px solid ${activo ? op.color : 'var(--border)'}`, background: activo ? op.color + '26' : 'transparent', color: activo ? op.color : 'var(--text-secondary)' }}>
                {op.label}
              </button>
            );
          })}
        </div>
      </div>

      {form.seguimiento === 'emitio' && (
        <div className="form-group" style={{ maxWidth: 320 }}>
          <label className="form-label">¿Con qué compañía emitió?</label>
          <select value={form.emitioCon || ''} onChange={e => set('emitioCon', e.target.value)}>
            <option value="">Elegí la compañía</option>
            {[...companiasCotizadas, ...(form.emitioCon && !companiasCotizadas.includes(form.emitioCon) ? [form.emitioCon] : [])].map(c => <option key={c} value={c}>{c}</option>)}
            <option value="Otra">Otra</option>
          </select>
        </div>
      )}

      <EmisionFutura form={form} set={set} />

      {(form.seguimiento === 'volver_contactar' || form.seguimiento === 'contactado' || form.seguimiento === 'sin_respuesta') && (
        <div className="form-group" style={{ maxWidth: 260 }}>
          <label className="form-label">Volver a contactar el</label>
          <input type="date" value={form.proximoContacto || ''} onChange={e => set('proximoContacto', e.target.value)} />
        </div>
      )}

      {/* Registrar un contacto */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Registrar un contacto</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {CANALES.map(c => (
            <button key={c} type="button" onClick={() => setCanal(c)}
              style={{ fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer', borderRadius: 8, padding: '6px 12px',
                border: `1px solid ${canal === c ? 'var(--accent)' : 'var(--border)'}`, background: canal === c ? 'var(--accent-dim)' : 'transparent', color: canal === c ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: canal === c ? 700 : 500 }}>
              {c}
            </button>
          ))}
        </div>
        <textarea rows={3} value={nota} onChange={e => setNota(e.target.value)} placeholder="¿Qué respondió? Ej.: lo ve con la esposa, lo llamo el viernes." style={{ width: '100%', resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <select value={nuevoEstado} onChange={e => setNuevoEstado(e.target.value)} style={{ flex: '1 1 240px' }}>
            <option value="">Estado después del contacto: {segDe(form.seguimiento && form.seguimiento !== 'sin_contactar' ? form.seguimiento : 'contactado').label}</option>
            {SEGUIMIENTO.filter(o => o.id !== 'sin_contactar').map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button type="button" className="btn btn-primary btn-sm" onClick={agregar} disabled={!nota.trim() && !nuevoEstado}>Agregar al registro</button>
        </div>
      </div>

      {/* Historial */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Historial {log.length ? `(${log.length})` : ''}</div>
        {log.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Todavía no se registró ningún contacto. Estado actual: <b style={{ color: estado.color }}>{estado.label}</b>.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {log.map((e, i) => {
              const es = segDe(e.estado);
              return (
                <div key={i} style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 10, borderLeft: `3px solid ${es.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                    <span>{fechaHora(e.fecha)} · {e.canal}{e.autor ? ` · ${e.autor}` : ''}</span>
                    <span style={{ color: es.color, fontWeight: 700 }}>{es.label}</span>
                  </div>
                  {e.nota && <div style={{ fontSize: 13.5, marginTop: 4, whiteSpace: 'pre-wrap' }}>{e.nota}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
const COBERTURAS = ['Todo Riesgo', 'Terceros Completo', 'Terceros Básico', 'RC Básica'];

// Beneficios estándar por compañía (fijos, según comparativo NEBB).
// Las claves de compañía se comparan en mayúsculas y sin tildes.
const BENEFICIOS_ORDEN = [
  'Auxilio Mecánico', 'Asistencia Mercosur', 'Reposición 0 Km',
  'Repuestos originales', 'Cobertura Cristales', 'Cobertura Cerradura', 'Auto sustituto', 'Accidentes Personales',
];
const BENEFICIOS_FIJOS = {
  'SBI':          { 'Auxilio Mecánico': 'Ilimitado', 'Asistencia Mercosur': 'Sí', 'Reposición 0 Km': 'Hasta 2 años', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'U$S 200', 'Cobertura Cerradura': 'U$S 200', 'Auto sustituto': '14 días (hurto o pérdida total)', 'Accidentes Personales': 'U$S 15.000' },
  'SAN CRISTOBAL':{ 'Auxilio Mecánico': 'Ilimitado', 'Asistencia Mercosur': 'Hasta 300KM', 'Reposición 0 Km': 'Hasta 2 años', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'U$S 200', 'Cobertura Cerradura': 'U$S 200', 'Auto sustituto': 'Opcional', 'Accidentes Personales': 'U$S 20.000' },
  // SURA tiene dos planes de Todo Riesgo: Total y Total Plus (ver PLANES_DOBLES).
  'SURA':         { 'Auxilio Mecánico': 'Ilimitado', 'Asistencia Mercosur': 'Incluido', 'Reposición 0 Km': 'Hasta 2 años', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'U$S 200 (1 vez al año)', 'Cobertura Cerradura': 'U$S 200 (1 vez al año)', 'Auto sustituto': 'Opcional', 'Accidentes Personales': 'Incluido' },
  'MAPFRE':       { 'Auxilio Mecánico': 'Ilimitado', 'Asistencia Mercosur': 'Incluido en cobertura Total', 'Reposición 0 Km': 'Hasta 2 años', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'U$S 200', 'Cobertura Cerradura': 'U$S 200', 'Auto sustituto': 'U$S 50 por día', 'Accidentes Personales': '—' },
  'BSE':          { 'Auxilio Mecánico': '10 Servicios', 'Asistencia Mercosur': 'Incluido en cobertura Total', 'Reposición 0 Km': 'Hasta 5 años', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'U$S 200', 'Cobertura Cerradura': 'U$S 200', 'Auto sustituto': 'Opcional', 'Accidentes Personales': '—' },
  'PORTO SEGURO': { 'Auxilio Mecánico': 'Ilimitado', 'Asistencia Mercosur': 'Incluido en cobertura Total', 'Reposición 0 Km': 'Hasta 1 año', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'Opcional', 'Cobertura Cerradura': 'Opcional', 'Auto sustituto': 'No incluido', 'Accidentes Personales': '—' },
  'SANCOR':       { 'Auxilio Mecánico': 'Ilimitado', 'Asistencia Mercosur': 'Incluido en cobertura Total', 'Reposición 0 Km': 'Hasta 1 año', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'Opcional', 'Cobertura Cerradura': 'Opcional', 'Auto sustituto': 'Opcional', 'Accidentes Personales': '—' },
  // Barbuss TODO RIESGO (webuy.barbussrisk.com.uy, actualizado 19/10/2026).
  'BARBUSS':      { 'Auxilio Mecánico': '6 por año (2 por mes)', 'Asistencia Mercosur': 'Grúa libre en Argentina y Brasil', 'Reposición 0 Km': 'Hasta 2 años', 'Repuestos originales': 'Hasta 3 años (5 si es 0 km)', 'Cobertura Cristales': 'U$S 100 parabrisas · U$S 100 laterales', 'Cobertura Cerradura': 'U$S 100', 'Auto sustituto': 'Opcional (15 días)', 'Accidentes Personales': 'U$S 10.000' },
  // Berkley TODO RIESGO = cobertura 04/05 (berkley.com.uy, seguro-auto-protegido, 26/10/2026).
  // Accidentes personales (U$S 10.000, Art. 53) y vehículo de cortesía (15 días) salen de las condiciones generales 18/03/2026.
  // Cristales U$S 150, cerradura U$S 150 y reposición 0 km hasta 2 años: datos de Nico (26/10/2026); no figuran en la página ni en las condiciones.
  'BERKLEY':      { 'Auxilio Mecánico': 'Incluido', 'Asistencia Mercosur': 'Incluido', 'Reposición 0 Km': 'Hasta 2 años', 'Repuestos originales': 'Hasta 5 años', 'Cobertura Cristales': 'U$S 150', 'Cobertura Cerradura': 'U$S 150', 'Auto sustituto': 'Opcional (hasta 15 días)', 'Accidentes Personales': 'U$S 10.000' },
};
// Compañías que cotizan DOS planes de Todo Riesgo: uno base y uno "plus",
// más caro y con más beneficios. En el PDF cada plan lleva su nombre, van en
// dos columnas en el cuadro de beneficios y lo que suma el plus se marca en
// verde, para que se entienda por qué cuesta más.
//  - esPlus / esBase: reconocen el plan por su nombre, si viene.
//  - mismoDeducible: si vienen dos filas sin nombre, solo se toman como
//    base + plus cuando tienen el mismo deducible (si no, son el mismo plan
//    con distintos deducibles). La más cara es el plus.
const PLANES_DOBLES = [
  {
    clave: 'SURA', base: 'Total', plus: 'Total Plus', mismoDeducible: false,
    esPlus: (p) => /plus/i.test(p), esBase: (p) => /total/i.test(p),
    beneficios: {
      'Total': BENEFICIOS_FIJOS['SURA'],
      'Total Plus': { ...BENEFICIOS_FIJOS['SURA'], 'Cobertura Cristales': 'U$S 400 (1 vez al año)', 'Cobertura Cerradura': 'U$S 400 (1 vez al año)', 'Auto sustituto': 'Incluido' },
    },
    nota: 'cristales y cerraduras hasta U$S 400 (en vez de U$S 200) y auto sustituto incluido',
  },
  {
    // Barbuss: TODO RIESGO y AUTO+ (webuy.barbussrisk.com.uy, 20/10/2026).
    clave: 'BARBUSS', base: 'Todo Riesgo', plus: 'AUTO+', mismoDeducible: true,
    esPlus: (p) => /auto\s*\+|plus/i.test(p), esBase: (p) => /todo\s*riesgo/i.test(p),
    beneficios: {
      'Todo Riesgo': BENEFICIOS_FIJOS['BARBUSS'],
      'AUTO+': { ...BENEFICIOS_FIJOS['BARBUSS'], 'Auxilio Mecánico': 'Ilimitado', 'Reposición 0 Km': 'Hasta 3 años (5 si es 0 km)', 'Repuestos originales': 'Siempre', 'Cobertura Cristales': 'U$S 300 parabrisas · U$S 200 laterales', 'Cobertura Cerradura': 'U$S 200', 'Auto sustituto': '15 días por evento' },
    },
    nota: 'auxilio ilimitado, más cobertura de cristales y cerraduras, auto de alquiler 15 días por evento, repuestos originales siempre y reposición 0 km por más años',
  },
];
const normalizeCo = (s) => (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

// ── Bonificación adicional (Porto, Barbuss) ───────────────────
// Algunas compañías autorizan un descuento extra sobre su precio (Porto con
// la cédula del cliente; Barbuss para igualar a la competencia e incluso
// mejorarla). Se aplica sobre los precios ORIGINALES
// (guardados en `sinBonif`), así que cambiar de 10% a 15% no acumula
// descuentos y "Quitar" vuelve exacto al precio de Wink.
const CAMPOS_PRECIO = ['contado', 'cuotas2', 'cuotas3', 'cuotas6', 'cuotas10'];
// ── Opciones de BSE por limitación de edad ────────────────────
// El PDF agrega dos filas de BSE con descuento por rango de edad. No tiene
// sentido mostrarle a una persona de 20 años la de 56 a 75. Se pueden
// apagar a mano (quote.bseEdad = { r35, r56 }); si no se tocaron, se apagan
// solas cuando la edad del cliente está fuera del rango.
const RANGOS_EDAD_BSE = [
  { k: 'r35', desde: 35, hasta: 55, pct: 0.06, etiqueta: 'Limitación edad 35 a 55 años (−6%)', corto: '35 a 55 años (−6%)' },
  { k: 'r56', desde: 56, hasta: 75, pct: 0.08, etiqueta: 'Limitación edad 56 a 75 años (−8%)', corto: '56 a 75 años (−8%)' },
];
function rangoEdadActivo(quote, r) {
  const manual = quote.bseEdad && quote.bseEdad[r.k];
  if (typeof manual === 'boolean') return manual;
  const edad = Number(quote.edad);
  if (!(edad > 0)) return true; // sin edad cargada: se muestran, como siempre
  return edad >= r.desde && edad <= r.hasta;
}

// Compañías que aceptan bonificación. Para sumar otra, agregarla acá.
const CIAS_BONIF = [
  { clave: 'PORTO', nombre: 'Porto' },
  { clave: 'BARBUSS', nombre: 'Barbuss' },
];
const ciaBonif = (c) => CIAS_BONIF.find(x => normalizeCo(c).includes(x.clave)) || null;
function aplicarBonificacion(fila, pct) {
  const base = fila.sinBonif || Object.fromEntries(CAMPOS_PRECIO.map(k => [k, fila[k]]));
  const p = Number(pct);
  if (!(p > 0)) {
    const { sinBonif, bonificacion, ...resto } = fila;
    return { ...resto, ...base };
  }
  const nuevos = {};
  for (const k of CAMPOS_PRECIO) {
    const v = base[k];
    const n = Number(v);
    nuevos[k] = v === '' || v == null || !Number.isFinite(n) ? (v ?? '') : String(Math.round(n * (1 - p / 100)));
  }
  return { ...fila, ...nuevos, sinBonif: base, bonificacion: p };
}
const planDobleDe = (company) => PLANES_DOBLES.find(d => normalizeCo(company).includes(d.clave)) || null;
function beneficiosDe(company, variante) {
  const key = normalizeCo(company);
  const doble = planDobleDe(company);
  if (doble && variante && doble.beneficios[variante]) return doble.beneficios[variante];
  for (const k of Object.keys(BENEFICIOS_FIJOS)) {
    if (key.includes(normalizeCo(k)) || normalizeCo(k).includes(key)) return BENEFICIOS_FIJOS[k];
  }
  return null;
}

// ── Genera HTML para PDF ──────────────────────────────────────
function generateQuoteHTML(quote, companies) {
  const _d = new Date();
  const _meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  const today = `${_d.getDate()} de ${_meses[_d.getMonth()]} de ${_d.getFullYear()}`;
  const rawRows = quote.prices || [];

  // "$U" es la moneda local, que `money()` ya escribe como "$": repetirla
  // daba "$80.000 $U". Las notas que aportan algo ("U$S", "+ IVA") quedan.
  const notaDeducible = (nota) => {
    const t = (nota || '').trim();
    // Las notas internas del cotizador ("...Wink...") no van al PDF del cliente.
    return /^\$U?\.?$/i.test(t) || /wink/i.test(t) ? '' : t;
  };
  // Notas que ve el cliente: sin las partes internas del cotizador ("Sin precio en Wink…").
  const notasCliente = (n) => String(n || '').split(' · ').filter(x => !/wink/i.test(x)).join(' · ');
  const money = (v) => v !== '' && v != null && !isNaN(Number(v)) ? '$' + Number(v).toLocaleString('es-UY') : (v || '—');
  const num = (v) => { const n = Number(v); return isNaN(n) ? null : n; };
  // Berkley y Sancor cotizan el DEDUCIBLE en dólares (Nico, 07/08 y 27/10/2026): Wink
  // no lo aclara y sin esto salía "$1.260" en vez de "U$S 1.260". Misma lista que
  // DEDUCIBLE_EN_USD de cotizacionPublica.js: si se suma una compañía, va en las dos.
  const dedEnUSD = (c) => /^\s*(berkley|sancor)/i.test(String(c || ''));
  const dedMoney = (r) => dedEnUSD(r.company) ? 'U$S ' + Number(r.deducible).toLocaleString('es-UY') : money(r.deducible);
  const dedNota = (r) => { const n = notaDeducible(r.deducibleNota); return dedEnUSD(r.company) && /u\$s?/i.test(n) ? '' : n; };
  const isBSE = (c) => normalizeCo(c).includes('BSE') || normalizeCo(c).includes('BANCO DE SEGUROS');
  const isSURA = (c) => normalizeCo(c).includes('SURA');
  const pctCols = (r, pct) => {
    const apply = (v) => { const n = num(v); return n != null ? String(Math.round(n * (1 - pct))) : ''; };
    return { ...r, contado: apply(r.contado), cuotas2: apply(r.cuotas2), cuotas3: apply(r.cuotas3), cuotas6: apply(r.cuotas6), cuotas10: apply(r.cuotas10) };
  };

  // SURA cotiza en el Wink el valor de CADA cuota, no el total como las demás
  // compañías. Para que sea comparable, el total de cada modalidad = valor de
  // cuota × cantidad de cuotas. El CONTADO de SURA ya es total: no se toca.
  // (El Wink no lo señala; es una regla fija que la oficina conoce.)
  const corregirSURA = (r) => {
    if (!isSURA(r.company)) return r;
    const porCuota = (v, n) => {
      // Celda vacía/sin valor: se mantiene vacía (no multiplicar a "0").
      if (v === '' || v == null) return '';
      const x = num(v);
      return x != null ? String(Math.round(x * n)) : (v || '');
    };
    return {
      ...r,
      cuotas2:  porCuota(r.cuotas2, 2),
      cuotas3:  porCuota(r.cuotas3, 3),
      cuotas6:  porCuota(r.cuotas6, 6),
      cuotas10: porCuota(r.cuotas10, 10),
    };
  };

  // Procesar BSE: detectar 3x2 (la más cara), base (la más barata sin 3x2), y generar filas de edad
  const bseRows = rawRows.filter(r => isBSE(r.company));
  const otrosRows = rawRows.filter(r => !isBSE(r.company)).map(corregirSURA);
  // SURA / Barbuss: cuál fila es el plan base y cuál el plus. Si el plan lo
  // dice, manda; si vienen dos sin nombre, la más cara es el plus (en Barbuss,
  // solo si las dos tienen el mismo deducible).
  for (const d of PLANES_DOBLES) {
    const filas = otrosRows.filter(r => normalizeCo(r.company).includes(d.clave));
    if (!filas.length) continue;
    const conNombre = (r) => d.esPlus(r.plan || '') ? d.plus : (d.esBase(r.plan || '') ? d.base : null);
    const porPrecio = [...filas].sort((a, b) => (num(a.contado) || 0) - (num(b.contado) || 0));
    const mismoDed = filas.length === 2 && String(num(filas[0].deducible)) === String(num(filas[1].deducible));
    const parSinNombre = filas.length === 2 && (!d.mismoDeducible || mismoDed);
    for (const r of filas) {
      let v = conNombre(r);
      if (!v && parSinNombre) v = r === porPrecio[1] ? d.plus : d.base;
      if (!v) continue;
      r.variante = v;
      r.esPlus = v === d.plus;
      r.plan = r.esPlus ? `${d.plus} · más beneficios (ver hoja 2)` : d.base;
    }
  }
  // Berkley en Todo Riesgo cotiza la MISMA cobertura con dos deducibles. Wink rotula
  // la segunda con el nombre largo de la póliza ("RC, HURTO P-T, INCENDIO P-T, DAÑO P-T"),
  // que al cliente le suena a otra cobertura: se muestra como lo que es (Nico, 27/10/2026).
  // Solo cambia el rótulo en pantalla: el precio, el deducible y lo que se emite no se tocan.
  {
    const berk = otrosRows.filter(r => /^\s*berkley/i.test(String(r.company || '')));
    const deds = berk.map(r => num(r.deducible)).filter(d => d != null && d > 0);
    if (/todo\s*riesgo/i.test(String(quote.cobertura || '')) && berk.length > 1 && deds.length === berk.length && Math.max(...deds) > Math.min(...deds)) {
      const minD = Math.min(...deds);
      for (const r of berk) r.plan = num(r.deducible) === minD ? 'Todo Riesgo · deducible reducido' : 'Todo Riesgo';
    }
  }
  let bseProcesadas = [];
  if (bseRows.length > 0) {
    const ordenadasBSE = [...bseRows].sort((a, b) => (num(a.contado) || 0) - (num(b.contado) || 0));
    const base = ordenadasBSE[0];
    const tresPorDos = bseRows.length > 1 ? ordenadasBSE[ordenadasBSE.length - 1] : null;
    // Fila base estándar
    bseProcesadas.push({ ...base, company: 'BSE', plan: 'Estándar' });
    // Limitación de edad (calculadas sobre la base), solo las que aplican
    for (const r of RANGOS_EDAD_BSE) {
      if (rangoEdadActivo(quote, r)) bseProcesadas.push({ ...pctCols(base, r.pct), company: 'BSE', plan: r.etiqueta });
    }
    // 3x2 (la más cara) en negrita
    if (tresPorDos && tresPorDos !== base) {
      bseProcesadas.push({ ...tresPorDos, company: 'BSE', plan: '3x2 — 3 años por el costo de 2', destacar: true });
    }
  }

  // Ordenar todo por precio contado ascendente (BSE procesadas + otros), 3x2 PRIMERO (lo pidió Nicolás: es la opción a destacar)
  const rows = [...otrosRows, ...bseProcesadas]
    .sort((a, b) => {
      if (a.destacar && !b.destacar) return -1;
      if (!a.destacar && b.destacar) return 1;
      return (num(a.contado) || 999999999) - (num(b.contado) || 999999999);
    });

  // Diferencia % respecto a la opción más barata
  const minContado = Math.min(...rows.filter(r => num(r.contado) != null && !r.destacar).map(r => num(r.contado)));
  const difPct = (r) => {
    const n = num(r.contado);
    if (n == null || !isFinite(minContado) || minContado === 0) return '';
    const d = ((n - minContado) / minContado) * 100;
    return d === 0 ? 'Base' : `+${d.toFixed(1)}%`;
  };

  // El logo de la compañía, o su nombre en texto si todavía no lo tenemos:
  // una compañía nueva sale sin logo, nunca con un hueco.
  const logoCelda = (c, clase = 'logo') => {
    const src = logoDe(c);
    return src ? `<img class="${clase}" src="${src}" alt="${c}">` : `<strong>${c}</strong>`;
  };

  const tableRows = rows.map((r, i) => `
    <tr class="${r.destacar ? 'destacar' : ''}">
      <td class="comp">${logoCelda(r.company)}${r.plan ? `<span class="plan${r.esPlus ? ' tp' : ''}">${r.plan}</span>` : ''}${r.bonificacion ? `<span class="plan bonif">Incluye bonificación adicional de ${r.bonificacion}%</span>` : ''}</td>
      <td class="contado">${money(r.contado)}</td>
      <td>${money(r.cuotas2)}</td>
      <td>${money(r.cuotas3)}</td>
      <td>${money(r.cuotas6)}</td>
      <td>${money(r.cuotas10)}</td>
      <td class="ded">${(num(r.deducible) != null && num(r.deducible) > 0) ? dedMoney(r) + (dedNota(r) ? ' ' + dedNota(r) : '') : '<span class="falta">A confirmar</span>'}</td>
      <td class="dif ${difPct(r) === 'Base' ? 'base' : ''}">${difPct(r) === 'Base' ? '<span>Base</span>' : difPct(r)}</td>
    </tr>
  `).join('');

  // Los beneficios son por COMPAÑÍA, no por plan: una columna por compañía
  // (en el orden en que aparecen en la tabla de precios), sin repetir.
  // SURA y Barbuss pueden ocupar dos columnas (base y plus) si se cotizaron los dos.
  const compBeneficios = [];
  for (const r of rows) {
    if (!beneficiosDe(r.company, r.variante)) continue;
    const variante = r.variante || '';
    if (!compBeneficios.some(c => normalizeCo(c.company) === normalizeCo(r.company) && c.variante === variante)) compBeneficios.push({ company: r.company, variante });
  }
  // Las compañías con base y plus cotizados: plus pegado a la derecha del base.
  const dobles = PLANES_DOBLES.filter(d =>
    compBeneficios.some(c => c.variante === d.base && normalizeCo(c.company).includes(d.clave)) &&
    compBeneficios.some(c => c.variante === d.plus && normalizeCo(c.company).includes(d.clave)));
  for (const d of dobles) {
    const tp = compBeneficios.splice(compBeneficios.findIndex(c => c.variante === d.plus && normalizeCo(c.company).includes(d.clave)), 1)[0];
    compBeneficios.splice(compBeneficios.findIndex(c => c.variante === d.base && normalizeCo(c.company).includes(d.clave)) + 1, 0, tp);
  }
  const dobleDeCol = (c) => dobles.find(d => normalizeCo(c.company).includes(d.clave)) || null;
  const celdaBen = (c, bname) => {
    const b = beneficiosDe(c.company, c.variante);
    const v = (b && b[bname]) || 'Consultar';
    const d = dobleDeCol(c);
    if (d && c.variante === d.plus && d.beneficios[d.base][bname] !== v) return `<td class="sura-mejor">${v}</td>`;
    if (d && c.variante === d.base && d.beneficios[d.plus][bname] !== v) return `<td class="sura-menos">${v}</td>`;
    return `<td>${v}</td>`;
  };
  const benefitRows = BENEFICIOS_ORDEN.map(bname => `
    <tr>
      <td class="bname">${bname}</td>
      ${compBeneficios.map(c => celdaBen(c, bname)).join('')}
    </tr>
  `).join('');
  const nombreCia = { SURA: 'SURA', BARBUSS: 'Barbuss' };
  const notaSura = dobles.map(d => `<div class="nota-sura"><b>${nombreCia[d.clave]} cotiza dos planes de Todo Riesgo:</b> <b>${d.base}</b> (el más económico) y <b>${d.plus}</b>. En verde, lo que suma el ${d.plus}: ${d.nota}.</div>`).join('');

  // Filas de la comparativa "corredor vs. contratación directa" (hoja 3).
  // Es el mismo cuadro que se mandaba antes en el PDF armado a mano.
  const COMPARATIVO = [
    ['Asesoramiento previo',        [1, 'Personalizado'],          [0, 'Estándar'],             [0, 'Básico']],
    ['Comparación entre compañías', [1, 'Múltiples opciones'],     [0, 'Solo su propia compañía'], [0, 'Opciones limitadas']],
    ['Análisis de coberturas',      [1, 'Según tu perfil'],        [0, 'Genérico'],             [0, 'Genérico']],
    ['Revisión de cláusulas',       [1, 'Detallada'],              [0, 'Mínima'],               [0, 'Muy básica']],
    ['Gestión de siniestros',       [1, 'Acompañamiento completo'],[0, 'Call center'],          [0, 'No ofrecen seguimiento']],
    ['Negociaciones y beneficios',  [1, 'Acceso ampliado'],        [0, 'Restringido'],          [0, 'Restringido']],
    ['Atención personalizada',      [1, 'Referente fijo'],         [0, 'Impersonal'],           [0, 'Limitada']],
  ];
  const celdaComp = ([ok, txt]) => `<td class="${ok ? 'si' : 'no'}"><span class="ico">${ok ? '✔' : '✕'}</span>${txt}</td>`;
  const comparativoRows = COMPARATIVO.map(([svc, a, b, c]) => `
    <tr><td class="svc">${svc}</td>${celdaComp(a)}${celdaComp(b)}${celdaComp(c)}</tr>`).join('');

  const tieneBeneficios = compBeneficios.length > 0;
  const footerHTML = `
    <div class="footer">
      <div class="legal">Los precios son orientativos y pueden variar según las condiciones particulares del riesgo. Deducibles con IVA incluido. Validez de la cotización: 10 días dentro del mes de cálculo.</div>
      <div><strong>NEBB Brokers</strong> · Corredores de Seguros y Reaseguros · Juan Carlos Gómez 1388 of. 801, Montevideo · Tel. 2915 5202 · nicolas@nebbrokers.com · nebbrokers.com</div>
    </div>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Cotización de Seguro · NEBB Brokers</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  /* Paleta NEBB: azul petróleo del isologo + celeste del símbolo.
     Los colores de fondo se fuerzan en impresión (-webkit-print-color-adjust)
     para que el PDF no salga en blanco y negro. */
  @page { size: A4; margin: 0; }
  * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { background:#fff; }
  body { font-family: 'Poppins', 'Helvetica Neue', Arial, sans-serif; color:#243447; font-size:11.5px; line-height:1.55; }
  .page { width:210mm; min-height:297mm; padding:12mm 15mm 10mm; display:flex; flex-direction:column; page-break-after:always; break-after:page; }
  .page:last-child { page-break-after:auto; break-after:auto; }

  /* Cabecera: bloque de datos a la izquierda, logo a la derecha, filete celeste */
  .header { display:flex; justify-content:space-between; align-items:flex-end; padding-bottom:10px; margin-bottom:14px; border-bottom:2px solid #00ACEE; }
  .header .meta { font-size:11px; color:#5b6b7c; line-height:1.6; }
  .header .meta .doc { font-size:15px; font-weight:600; color:#0F4761; letter-spacing:.2px; display:block; margin-bottom:2px; }
  .header img { height:50px; width:auto; max-width:300px; }

  .greeting { margin-bottom:10px; color:#3a4a5c; }
  .greeting p { margin-bottom:5px; }
  .greeting strong { color:#0F4761; font-weight:600; }

  /* Títulos de sección como en la cotización de siempre: filete fino arriba, texto azul */
  h2.section { font-size:17px; font-weight:500; color:#0F4761; border-top:1px solid #0F4761; padding-top:7px; margin:14px 0 8px; letter-spacing:.1px; }
  h2.section:first-of-type { margin-top:0; }
  .header + h2.section { border-top:none; padding-top:0; }

  .vehicle-grid { display:grid; grid-template-columns:1fr 1fr; gap:3px 32px; margin-bottom:4px; }
  .vehicle-item { font-size:12px; color:#243447; }
  .vehicle-item label { font-weight:600; color:#0F4761; }
  .vehicle-item.wide { grid-column:1 / -1; }

  /* Tabla de cotizaciones: cabecera clara, líneas finas, zebra suave */
  table { width:100%; border-collapse:collapse; }
  table.cotiz, table.ben, table.comp { border:3px solid #0F4761; }
  table.cotiz tbody tr:last-child td, table.ben tbody tr:last-child td { border-bottom:none; }
  .cotiz thead th { color:#0F4761; font-weight:600; font-size:10.5px; padding:8px 6px; text-align:center; border-bottom:2px solid #0F4761; background:#f3f8fb; white-space:nowrap; }
  .cotiz th:first-child { text-align:left; padding-left:10px; }
  .cotiz tbody td { padding:4px 6px; text-align:center; font-size:11px; color:#243447; border-bottom:1px solid #cfdae4; }
  .cotiz tbody tr:nth-child(even) td { background:#f8fafc; }
  .cotiz td.comp { text-align:left; padding-left:10px; }
  .cotiz td.comp strong { color:#0F4761; font-weight:600; font-size:11.5px; }
  .cotiz .plan { display:block; font-size:8px; color:#6b7b8c; line-height:1.3; margin-top:1px; }
  .cotiz td.comp .logo { display:block; height:15px; width:auto; max-width:94px; }
  .cotiz td.contado { font-weight:600; color:#0F4761; background:rgba(0,172,238,.06); }
  .cotiz td.ded { font-size:10px; color:#5b6b7c; }
  .cotiz .falta { color:#b45309; font-style:italic; font-size:10px; }
  .cotiz td.dif { font-size:10.5px; font-weight:500; color:#E11F5D; }
  .cotiz td.dif.base span { display:inline-block; background:#0F4761; color:#fff; font-size:9px; font-weight:600; padding:2px 8px; border-radius:10px; letter-spacing:.3px; }
  .cotiz tbody tr.destacar td { background:#e6f6fd !important; font-weight:600; color:#0F4761; border-top:1.5px solid #00ACEE; border-bottom:1.5px solid #00ACEE; }
  .cotiz .plan.tp { color:#1a7f45; font-weight:700; }
  .cotiz tbody tr.destacar .plan { color:#0F4761; font-weight:500; }
  .cotiz .plan.bonif { color:#1f6b47; font-weight:600; }

  .notes { margin-top:8px; margin-bottom:10px; font-size:10.5px; color:#3a4a5c; }
  .notes p { padding:5px 12px; background:#f3f8fb; border-left:3px solid #00ACEE; margin-bottom:6px; }
  .notes p.legal { background:none; border:none; padding:0; color:#7a8794; font-size:9.5px; margin-top:6px; }

  /* Beneficios por compañía (hoja 2) */
  .ben thead th { color:#0F4761; font-weight:600; font-size:10.5px; padding:9px 6px; text-align:center; border-bottom:2px solid #0F4761; background:#f3f8fb; line-height:1.3; vertical-align:bottom; }
  .ben thead th:first-child { text-align:left; padding-left:10px; }
  .ben thead th .logo-ben { display:block; margin:0 auto; height:18px; width:auto; max-width:100%; }
  .ben tbody td { font-size:10px; padding:9px 6px; text-align:center; border-bottom:1px solid #cfdae4; color:#243447; line-height:1.3; }
  .ben tbody tr:nth-child(even) td { background:#f8fafc; }
  .ben thead th .var { display:block; margin-top:4px; font-size:9.5px; font-weight:700; color:#0F4761; }
  .ben thead th.sura-tp { background:#e3f5ea; }
  .ben thead th.sura-tp .var { color:#1a7f45; }
  .ben tbody td.sura-mejor, .ben tbody tr:nth-child(even) td.sura-mejor { background:#e3f5ea; color:#1a7f45; font-weight:700; }
  .ben tbody td.sura-menos { color:#8a97a5; }
  .nota-sura + .nota-sura { margin-top:6px; }
  .nota-sura { margin-top:12px; padding:10px 12px; font-size:10.5px; line-height:1.5; color:#243447; background:#f3fbf6; border-left:3px solid #1a7f45; border-radius:4px; }
  .ben td.bname { text-align:left; padding-left:10px; font-weight:600; color:#0F4761; font-size:10.5px; white-space:nowrap; }

  /* Comparativo corredor vs. directa (hoja 3) */
  h1.compara { font-size:26px; font-weight:500; color:#0F4761; line-height:1.25; margin-bottom:14px; }
  .lead { font-size:12px; color:#3a4a5c; margin-bottom:22px; }
  .lead strong { color:#0F4761; font-weight:600; }
  h3.sub { font-size:14px; font-weight:600; color:#243447; margin:0 0 10px; }
  .comp thead th { text-align:left; font-size:10.5px; font-weight:600; color:#0F4761; padding:8px 10px; border-bottom:2px solid #0F4761; background:#f3f8fb; }
  .comp thead th.corr { color:#fff; background:#0F4761; }
  .comp tbody td { font-size:10.5px; padding:8px 10px; border-bottom:1px solid #cfdae4; vertical-align:middle; color:#3a4a5c; }
  .comp td.svc { font-weight:600; color:#243447; width:26%; }
  .comp td.si { background:#f3fbfe; color:#0F4761; font-weight:500; }
  .comp .ico { display:inline-block; width:16px; font-weight:700; }
  .comp td.si .ico { color:#00ACEE; }
  .comp td.no .ico { color:#E11F5D; }
  .comp tfoot td { padding:9px 10px; font-size:10.5px; font-weight:600; color:#0F4761; background:#e6f6fd; }
  .valor { margin-top:26px; }
  .valor p { font-size:11.5px; color:#3a4a5c; margin-bottom:8px; }
  .valor ul { margin:6px 0 10px 18px; font-size:11.5px; color:#3a4a5c; }
  .valor li { margin-bottom:3px; }
  .cierre { margin-top:18px; padding:14px 18px; background:#0F4761; color:#fff; border-radius:6px; font-size:12px; line-height:1.6; }
  .cierre strong { color:#7fd9fb; }

  .footer { margin-top:auto; padding-top:6px; border-top:1px solid #d9e1e8; font-size:8.5px; color:#7a8794; line-height:1.5; }
  .footer .legal { margin-bottom:2px; }
  .footer strong { color:#0F4761; }

  @media print {
    .page { page-break-after:always; }
    .page:last-child { page-break-after:auto; }
    table, tr { page-break-inside:avoid; }
  }
</style>
</head>
<body>
  <!-- HOJA 1: Vehículo y precios -->
  <div class="page">
    <div class="header">
      <div class="meta">
        <span class="doc">Cotización de Seguro Automotor</span>
        ${today}<br>Validez: 10 días
      </div>
      <img src="${NEBB_LOGO}" alt="NEBB Brokers" />
    </div>

    <div class="greeting">
      <p>Estimado/a <strong>${quote.clientName || 'Cliente'}</strong>,</p>
      <p>Gracias por contactarnos para solicitar una cotización para el seguro de su vehículo. En <strong>NEBB Brokers</strong> nos comprometemos a ofrecerle las mejores opciones de cobertura que se adapten a sus necesidades.</p>
      <p>En este documento encontrará un resumen detallado de las diferentes opciones disponibles, ordenadas de menor a mayor costo, junto con sus respectivos precios y beneficios.</p>
    </div>

    <h2 class="section">Detalle del Vehículo</h2>
    <div class="vehicle-grid">
      ${quote.marca ? `<div class="vehicle-item"><label>Marca:</label> ${quote.marca}</div>` : ''}
      ${quote.anio ? `<div class="vehicle-item"><label>Año:</label> ${quote.anio}</div>` : ''}
      ${quote.modelo ? `<div class="vehicle-item wide"><label>Modelo:</label> ${quote.modelo}</div>` : ''}
      ${quote.zona ? `<div class="vehicle-item"><label>Zona de Circulación:</label> ${quote.zona}</div>` : ''}
      ${quote.uso ? `<div class="vehicle-item"><label>Uso:</label> ${quote.uso}</div>` : ''}
      ${quote.cobertura ? `<div class="vehicle-item"><label>Cobertura:</label> ${quote.cobertura}</div>` : ''}
    </div>

    <h2 class="section">Detalle de Cotizaciones</h2>
    <table class="cotiz">
      <thead>
        <tr>
          <th>Compañía</th>
          <th>Contado</th>
          <th>2 cuotas</th>
          <th>3 cuotas</th>
          <th>6 cuotas</th>
          <th>10 cuotas</th>
          <th>Deducible</th>
          <th>Dif. %</th>
        </tr>
      </thead>
      <tbody>${tableRows}</tbody>
    </table>
    <div class="notes">
      ${rows.some(r => /\bbse\b|banco de seguros/i.test(r.company || '')) ? '<p><strong>El deducible del Banco de Seguros es IVA incluido.</strong></p>' : ''}
      ${rows.some(r => /Limitación edad/i.test(r.plan || '')) ? '<p>Las opciones de BSE por limitación de edad son valores estimados según el rango etario; el precio final se confirma con la compañía.</p>' : ''}
      ${notasCliente(quote.notes) ? `<p>${notasCliente(quote.notes)}</p>` : '<p>Pagos con tarjeta: BSE 10 cuotas sin recargo, SURA 10 cuotas sin recargo. Deducibles con IVA incluido.</p>'}
    </div>
    ${footerHTML}
  </div>

  ${tieneBeneficios ? `
  <!-- HOJA 2: Beneficios por compañía -->
  <div class="page">
    <div class="header">
      <div class="meta"><span class="doc">Beneficios por Compañía</span>${today}</div>
      <img src="${NEBB_LOGO}" alt="NEBB Brokers" />
    </div>
    <h2 class="section">Detalle de Beneficios por Compañía</h2>
    <table class="ben">
      <thead>
        <tr>
          <th>Beneficio</th>
          ${compBeneficios.map(c => `<th class="${dobleDeCol(c) && c.variante === dobleDeCol(c).plus ? 'sura-tp' : ''}">${logoCelda(c.company, 'logo-ben')}${c.variante && dobleDeCol(c) ? `<span class="var">${c.variante}</span>` : ''}</th>`).join('')}
        </tr>
      </thead>
      <tbody>${benefitRows}</tbody>
    </table>
    ${notaSura}
    ${footerHTML}
  </div>
  ` : ''}

  <!-- HOJA 3: Por qué un corredor -->
  <div class="page">
    <div class="header">
      <div class="meta"><span class="doc">Corredor vs. Contratación Directa</span>${today}</div>
      <img src="${NEBB_LOGO}" alt="NEBB Brokers" />
    </div>
    <h1 class="compara">Comparativo: Corredor de Seguros<br>vs. Contratación Directa</h1>
    <p class="lead">Contar con un <strong>corredor de seguros</strong> te brinda asesoramiento profesional, acompañamiento continuo y defensa de tus intereses. Todo esto <strong>sin costo adicional</strong>, ya que la póliza tiene exactamente el mismo precio.</p>

    <h3 class="sub">Comparación de Servicios</h3>
    <table class="comp">
      <thead>
        <tr><th>Servicio / Beneficio</th><th class="corr">Corredor de Seguros</th><th>Directo con Aseguradora</th><th>Automotora</th></tr>
      </thead>
      <tbody>${comparativoRows}</tbody>
      <tfoot>
        <tr><td>Costo del servicio</td><td>Sin costo adicional</td><td>—</td><td>—</td></tr>
      </tfoot>
    </table>

    <div class="valor">
      <h3 class="sub">Valor agregado de contar con un corredor de seguros</h3>
      <p>Más allá de comparar pólizas, un corredor se convierte en un aliado que te acompaña durante toda la vida útil de tu seguro, asegurando que siempre cuentes con:</p>
      <ul>
        <li>Información clara y objetiva.</li>
        <li>Actualización y revisión de tu póliza cuando cambian tus necesidades.</li>
        <li>Gestión completa en situaciones que requieren rapidez y precisión.</li>
      </ul>
      <p>Elegir un corredor no implica un costo adicional, pero sí marca una diferencia significativa en tranquilidad, respaldo y eficiencia.</p>
    </div>
    <div class="cierre">Nuestro objetivo es que estés <strong>protegido</strong>, bien <strong>asesorado</strong> y con la mejor relación entre <strong>precio y cobertura</strong>. Quedamos a disposición para avanzar con la opción que prefieras.</div>
    ${footerHTML}
  </div>
</body>
</html>`;
}

// ── Entrega del PDF ───────────────────────────────────────────
// Pide el PDF al servidor (lo imprime Chromium, siempre con colores) y lo
// descarga. Si el servidor no puede, abre la vista de impresión de siempre
// y, si el navegador bloquea la ventana, descarga el HTML.
// `copia` (opcional): { quoteId, clave, etiqueta, prices } hace que el servidor
// guarde también una copia para el link que ve el cliente.
async function entregarPDF(html, nombreArchivo, copia) {
  try {
    const token = localStorage.getItem('nebb_token');
    const r = await fetch('/api/quotes/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ html, nombre: nombreArchivo, ...(copia || {}) }),
    });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const blob = await r.blob();
    if (!/pdf/i.test(blob.type)) throw new Error('la respuesta no es un PDF');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  } catch (e) {
    console.warn('[cotización] no se pudo generar el PDF en el servidor, se imprime desde el navegador:', e.message);
  }
  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch (e) {} }, 600);
  } else {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivo.replace(/\.pdf$/i, '') + '.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    alert('Tu navegador bloqueó la ventana emergente. Se descargó la cotización como archivo; abrila para imprimir o guardar como PDF.');
  }
}

// ── Modal nueva cotización ────────────────────────────────────
function QuoteModal({ quote, onClose, onSaveNew, onSaveEdit }) {
  const { addCeoMetric, companies, clients, showToast } = useApp();
  const isEdit = !!quote;
  const [step, setStep] = useState(isEdit ? 0 : 1);
  const pasos = isEdit ? [0, 1, 2] : [1, 2];
  const nombrePaso = { 0: 'Seguimiento', 1: '1. Bien', 2: '2. Precios' };
  const { currentUser } = useApp();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) await onSaveEdit(quote.id, (form.seguimiento && form.seguimiento !== 'sin_contactar') ? { ...form, nueva: false } : form);
      else await onSaveNew(form);
      onClose();
    } catch (e) {
      showToast('Error al guardar la cotización', 'error');
      setSaving(false);
    }
  };

  const [form, setForm] = useState(quote ? {
    ...quote,
    clientEmail: quote.clientEmail || quote.email || '',
    clientPhone: quote.clientPhone || quote.telefono || '',
  } : {
    clientName: '', clientEmail: '', clientPhone: '',
    marca: '', modelo: '', anio: new Date().getFullYear(),
    zona: 'Montevideo', cobertura: 'Todo Riesgo', uso: 'Particular',
    notes: 'Pagos con tarjeta: BSE 10 cuotas sin recargo, SURA 10 cuotas sin recargo. Deducibles con IVA incluido.',
    prices: [],
    benefits: [],
    attachments: [], // PDFs del gestor adjuntos para revisión posterior
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [newCompany, setNewCompany] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState('');
  const [iaUsada, setIaUsada] = useState(false); // marca que los precios vinieron de extracción IA
  const pdfRef = useRef();
  const attachRef = useRef();

  // Adjunta un PDF a la cotización (queda guardado para revisarlo después).
  // Límite 4MB por archivo y 12MB total; evita duplicar el mismo archivo.
  const MAX_FILE = 4 * 1024 * 1024, MAX_TOTAL = 12 * 1024 * 1024;
  const adjuntarPdf = (file, dataUrl) => {
    setForm(f => {
      const lista = f.attachments || [];
      if (lista.some(a => a.name === file.name && a.size === file.size)) return f; // ya está
      const total = lista.reduce((s, a) => s + (a.size || 0), 0);
      if (file.size > MAX_FILE) { setIaError(`"${file.name}" supera los 4MB y no se adjuntó (la extracción IA sí corre igual).`); return f; }
      if (total + file.size > MAX_TOTAL) { setIaError('Se alcanzó el límite de 12MB de adjuntos en esta cotización.'); return f; }
      return { ...f, attachments: [...lista, { name: file.name, size: file.size, data: dataUrl, uploadedAt: new Date().toISOString() }] };
    });
  };

  const verAdjunto = (a) => {
    // Abre el PDF en una pestaña nueva desde el base64 guardado
    const byteString = atob(a.data.split(',')[1]);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const quitarAdjunto = (i) => {
    if (!window.confirm('¿Eliminar este PDF adjunto de la cotización?')) return;
    setForm(f => ({ ...f, attachments: (f.attachments || []).filter((_, idx) => idx !== i) }));
  };

  const handleAdjuntarSinIA = (e) => {
    const f = e.target.files[0];
    if (!f || !f.name.toLowerCase().endsWith('.pdf')) { setIaError('Solo se aceptan archivos PDF'); return; }
    setIaError('');
    const r = new FileReader();
    r.onload = () => adjuntarPdf(f, r.result);
    r.onerror = () => setIaError('No se pudo leer el archivo. Probá de nuevo.');
    r.readAsDataURL(f);
  };

  const handleGestorPdf = async (e) => {
    const f = e.target.files[0];
    if (!f || !f.name.toLowerCase().endsWith('.pdf')) { setIaError('Solo se aceptan archivos PDF'); return; }
    setIaError(''); setIaLoading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(',')[1]);
        r.onerror = () => rej(new Error('Error al leer el archivo'));
        r.readAsDataURL(f);
      });
      // El PDF queda adjunto a la cotización para poder revisarlo después,
      // independientemente de que la extracción IA funcione o no.
      adjuntarPdf(f, `data:application/pdf;base64,${base64}`);

      // Hasta 3 intentos: los errores transitorios de la API (sobrecarga,
      // rate limit, cortes de red) son la causa típica de que "a veces ande
      // y a veces no". Entre intentos esperamos 2s y 4s.
      let parsed = null, lastErr = '';
      for (let intento = 1; intento <= 3 && !parsed; intento++) {
        if (intento > 1) await new Promise(r => setTimeout(r, intento * 2000));
        try {
          const response = await fetch('/api/anthropic', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(localStorage.getItem('nebb_token') ? { Authorization: `Bearer ${localStorage.getItem('nebb_token')}` } : {}),
            },
            body: JSON.stringify({
              model: 'claude-sonnet-4-5',
              max_tokens: 8000,
              messages: [{ role: 'user', content: [
              { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
              { type: 'text', text: `Este es un PDF de cotización de seguro de auto de un gestor (puede ser Wink u otro). Extraé los datos del vehículo y TODAS las filas de cotización (cada compañía/plan con sus precios). Respondé ÚNICAMENTE con un JSON válido, sin texto ni backticks, con esta estructura exacta:
{"marca":"","modelo":"","anio":"","zona":"","cobertura":"","prices":[{"company":"","plan":"","cobertura":"","contado":"","cuotas2":"","cuotas3":"","cuotas6":"","cuotas10":"","deducible":"","deducibleNota":""}]}

Instrucciones:
- marca, modelo, anio: del vehículo
- zona: zona de circulación (ej "Montevideo")
- cobertura: tipo general (ej "Todo Riesgo", "Cobertura Total"). Si el PDF cotiza UNA SOLA cobertura, va acá y dejá la cobertura de cada fila vacía.
- Si el PDF cotiza VARIAS coberturas (ej. una sección "Todo Riesgo" y otra "Triple" o "Total"), poné en CADA fila de prices su cobertura exacta en el campo "cobertura" de la fila, según la sección o columna a la que pertenece.
- prices: una entrada por cada fila de la tabla de cotización. Puede haber varias filas de la misma compañía con distinto deducible/plan — incluí TODAS.
- company: nombre EXACTO de la aseguradora según su logo o texto en esa fila. Compañías posibles: BSE (Banco de Seguros del Estado), Barbuss, Sancor (Sancor Seguros), SURA, SBI, Porto Seguro, Berkley, Mapfre. ATENCIÓN: "Sancor" y "BSE/Banco de Seguros" son compañías DISTINTAS — no las confundas. Mirá bien el logo/nombre de cada fila; un error de compañía es gravísimo.
- plan: si la fila tiene un identificador de plan (ej "3x2", "limit"), si no dejalo ""
- contado, cuotas2, cuotas3, cuotas6, cuotas10: el precio en cada modalidad de pago, solo números sin símbolos ni puntos de miles (ej "44403").

REGLAS CRÍTICAS sobre las columnas (¡muy importante, no las violes!):
1. Cada valor va EXACTAMENTE en la columna que le corresponde según su ENCABEZADO de columna (Contado, 3 cuotas, 6 cuotas, 10 cuotas, etc.), no por su posición relativa. NO corras ni desplaces valores hacia otra columna.
2. Si una compañía NO cotiza alguna modalidad (ej. no ofrece 10 cuotas), esa celda queda VACÍA: poné "" en esa columna. NO uses el valor de otra columna para rellenarla. NO muevas el deducible ni ningún otro número a una celda de cuotas vacía. Ejemplo real frecuente: una fila tiene "10 cuotas" VACÍO pero sí tiene "11 cuotas" con valor — en ese caso cuotas10 va "" y el valor de 11 cuotas NO se usa (no tenemos columna para 11 cuotas, ignoralo).
3. IGNORÁ por completo cualquier columna "11 cuotas" o modalidades que no sean contado/2/3/6/10. NUNCA pongas el valor de "11 cuotas" en cuotas10.
4. El DEDUCIBLE es SIEMPRE la última columna de cada fila y SIEMPRE tiene un valor (ningún seguro tiene deducible 0 ni vacío). Si te cuesta identificarlo, es el último monto de la fila, frecuentemente separado o con una nota como "+ IVA", "U$S", o un número de póliza al lado. El deducible NUNCA debe quedar vacío. Si una fila parece no tener deducible, revisá de nuevo: seguramente confundiste una celda de cuotas vacía con el deducible.
5. deducible: monto del deducible, solo número. Si es en dólares poné el número y aclaralo en deducibleNota.
6. deducibleNota: aclaración del deducible (ej "+ IVA", "U$S", "U$S 800"). Si no hay, "".
7. COHERENCIA DE PRECIOS: el pago en cuotas NUNCA es más barato que el contado. Si en una fila ves que "3/6/10 cuotas" da un número MENOR que el contado (ej. contado 48000 y 3 cuotas 16000), volvé a mirar esa fila columna por columna. Extraé lo que realmente figura sin corromperlo; no inventes ni "corrijas", pero asegurate de no haber confundido columnas.
8. COMPLETITUD: incluí TODAS las filas de TODAS las secciones de cobertura del PDF. No omitas ninguna compañía ni ninguna fila, aunque haya varias filas de la misma compañía con distinto deducible. Contá las filas del PDF y asegurate de devolver la misma cantidad.

Verificá fila por fila: (a) la compañía es la correcta, (b) cada precio está bajo su encabezado correcto, (c) el deducible está cargado, (d) no omitiste filas. Extraé exactamente los valores que ves, sin inventar.` }
            ] }],
            }),
          });
          const data = await response.json();
          if (data.error) throw new Error(data.error.message || 'Error de API');
          if (data.stop_reason === 'max_tokens') {
            const e = new Error('El PDF tiene demasiadas filas y la respuesta quedó incompleta. Probá dividir el PDF o cargar las filas restantes a mano.');
            e.code = 'max_tokens';
            throw e;
          }
          const txt = (data.content || []).map(b => b.text || '').join('').trim();
          const jsonMatch = txt.match(/\{[\s\S]*\}/);
          if (!jsonMatch) throw new Error('La IA no devolvió datos válidos.');
          parsed = JSON.parse(jsonMatch[0]);
        } catch (err) {
          lastErr = err.message;
          // El truncamiento por tamaño no se arregla reintentando
          if (err.code === 'max_tokens') break;
        }
      }
      if (!parsed) throw new Error(lastErr + ' (si no es por tamaño, puede ser una sobrecarga momentánea del servicio de IA; esperá un minuto y probá de nuevo)');
      setForm(f2 => ({
        ...f2,
        marca: parsed.marca || f2.marca,
        modelo: parsed.modelo || f2.modelo,
        anio: parsed.anio || f2.anio,
        zona: parsed.zona || f2.zona,
        cobertura: parsed.cobertura || f2.cobertura,
        prices: Array.isArray(parsed.prices) ? parsed.prices.map(p => ({
          company: p.company || '', plan: p.plan || '', cobertura: p.cobertura || '',
          contado: p.contado || '', cuotas2: p.cuotas2 || '', cuotas3: p.cuotas3 || '',
          cuotas6: p.cuotas6 || '', cuotas10: p.cuotas10 || '',
          deducible: p.deducible || '', deducibleNota: p.deducibleNota || '', benefitsMap: {},
        })) : f2.prices,
      }));
      setIaUsada(true);
      setStep(2);
    } catch (err) {
      setIaError('Error al procesar el PDF: ' + err.message);
    }
    setIaLoading(false);
  };

  const addCompanyRow = () => {
    if (!newCompany) return;
    setForm(f => ({ ...f, prices: [...f.prices, { company: newCompany, plan: '', cobertura: '', contado: '', cuotas2: '', cuotas3: '', cuotas6: '', cuotas10: '', deducible: '', deducibleNota: '', benefitsMap: {} }] }));
    setNewCompany('');
  };

  const updatePrice = (idx, field, val) => {
    const prices = [...form.prices];
    prices[idx] = { ...prices[idx], [field]: val };
    // Si se corrige a mano un precio con bonificación, el precio original
    // también cambia: lo recalculamos para que "Quitar" no vuelva a un valor viejo.
    if (CAMPOS_PRECIO.includes(field) && prices[idx].bonificacion) {
      const n = Number(val), b = prices[idx].bonificacion;
      prices[idx].sinBonif = { ...prices[idx].sinBonif,
        [field]: val === '' || !Number.isFinite(n) ? val : String(Math.round(n / (1 - b / 100))) };
    }
    setForm(f => ({ ...f, prices }));
  };

  // Bonificación por compañía: se aplica a todas las filas de esa compañía.
  const filasDe = (cia) => (form.prices || []).filter(p => ciaBonif(p.company)?.clave === cia.clave);
  const bonifDe = (cia) => filasDe(cia).find(p => p.bonificacion)?.bonificacion || '';
  const ciasConBonif = CIAS_BONIF.filter(c => filasDe(c).length > 0);
  const [bonifPct, setBonifPct] = useState({});
  const aplicarBonif = (cia, pct) => {
    const n = Number(String(pct).replace(',', '.'));
    if (pct !== 0 && !(n > 0 && n < 100)) { alert(`Poné el porcentaje que autorizó ${cia.nombre} (por ejemplo 10).`); return; }
    setForm(f => ({ ...f, prices: f.prices.map(p => ciaBonif(p.company)?.clave === cia.clave ? aplicarBonificacion(p, pct === 0 ? 0 : n) : p) }));
    setBonifPct(b => ({ ...b, [cia.clave]: '' }));
  };
  // "Igualar": qué % hace falta para que la opción más barata de esta compañía
  // (a su precio ORIGINAL) quede igual a la más barata de las demás, o un X%
  // por debajo. Se calcula sobre el contado.
  const contadoBase = (p) => Number((p.sinBonif && p.sinBonif.contado) ?? p.contado);
  const pctParaIgualar = (cia, extra = 0) => {
    const propias = filasDe(cia).map(contadoBase).filter(v => v > 0);
    const rivales = (form.prices || []).filter(p => ciaBonif(p.company)?.clave !== cia.clave && Number(p.contado) > 0);
    if (!propias.length || !rivales.length) return null;
    const base = Math.min(...propias);
    const rival = rivales.reduce((m, p) => Number(p.contado) < Number(m.contado) ? p : m);
    const objetivo = Number(rival.contado) * (1 - extra / 100);
    if (objetivo >= base) return { pct: 0, rival, objetivo };
    return { pct: Math.ceil(Math.round((1 - objetivo / base) * 1e6) / 1e3) / 10, rival, objetivo }; // hacia arriba a 0,1%
  };

  // Guarda la cotización con la bonificación y abre WhatsApp con el aviso al
  // cliente y el link a su cotización actualizada (el link muestra lo guardado,
  // por eso se guarda primero).
  const telCliente = form.clientPhone || form.telefono || form.celular || '';
  const mensajeBonif = (cia) => {
    const nombre = String(form.clientName || '').trim().split(/\s+/)[0] || '';
    const masBarata = filasDe(cia).filter(p => Number(p.contado) > 0)
      .sort((a, b) => Number(a.contado) - Number(b.contado))[0];
    const $ = (v) => '$' + Number(v).toLocaleString('es-UY');
    const vehiculo = [form.marca, form.modelo, form.anio].filter(Boolean).join(' ');
    // El link va SIEMPRE con el dominio propio, aunque el panel esté abierto
    // desde el de Railway (favoritos viejos, app del iPhone). Antes salía con
    // el dominio desde el que se abría el panel. En desarrollo, el local.
    const link = linkCotizacion(form.codigo);
    return `¡Hola${nombre ? ' ' + nombre : ''}! Te escribimos de NEBB Brokers. ` +
      `Buenas noticias: ${cia.nombre} aprobó una bonificación adicional de ${bonifDe(cia)}% para tu ${vehiculo || 'vehículo'}.` +
      (masBarata ? ` Con el descuento queda en ${$(masBarata.contado)} contado` +
        (masBarata.sinBonif && Number(masBarata.sinBonif.contado) > 0 ? ` (antes ${$(masBarata.sinBonif.contado)})` : '') + '.' : '') +
      (link ? `\n\nAcá tenés la cotización actualizada con todas las compañías, y desde ahí mismo podés pedir la emisión:\n${link}` : '') +
      '\n\nCualquier duda, te ayudamos por acá.';
  };
  const avisarBonifWhatsApp = async (cia) => {
    if (!numeroWA(telCliente)) { alert('La cotización no tiene un celular cargado.'); return; }
    // La ventana se abre ANTES de guardar: si se abre después, el navegador la bloquea.
    const ventana = window.open('about:blank', '_blank');
    const url = linkWA(telCliente) + (linkWA(telCliente).includes('?') ? '&' : '?') + 'text=' + encodeURIComponent(mensajeBonif(cia));
    setSaving(true);
    try {
      const est = (!form.seguimiento || form.seguimiento === 'sin_contactar') ? 'contactado' : form.seguimiento;
      const entrada = { fecha: new Date().toISOString(), canal: 'WhatsApp', estado: est,
        nota: `Aviso de bonificación ${cia.nombre} ${bonifDe(cia)}% con la cotización actualizada` };
      const datos = { ...form, nueva: false, seguimiento: est, seguimientoLog: [entrada, ...(form.seguimientoLog || [])] };
      if (isEdit) await onSaveEdit(quote.id, datos); else await onSaveNew(datos);
      if (ventana) ventana.location.href = url; else window.open(url, '_blank');
      onClose();
    } catch (e) {
      if (ventana) ventana.close();
      showToast('No se pudo guardar la cotización', 'error');
      setSaving(false);
    }
  };

  const removeCompany = (idx) => {
    setForm(f => ({ ...f, prices: f.prices.filter((_, i) => i !== idx) }));
  };

  // Cobertura efectiva de una fila: la propia o, si está vacía, la general del formulario
  const cobDeFila = (p) => (p.cobertura || '').trim() || (form.cobertura || '').trim() || 'General';
  const coberturasDistintas = [...new Set((form.prices || []).map(cobDeFila))];

  // Detecta datos sospechosos en una fila para que la oficina los revise contra
  // el cotizador original antes de enviar. Cubre los errores reales que vimos:
  //  - SURA: cuotas MENORES que el contado (dato corrupto de Wink).
  //  - BARBUSS: columna corrida (un valor de "11 cuotas" metido en "10 cuotas").
  //  - Sancor mal leído como "Banco Seguros" (la IA confunde compañías).
  //  - Deducible vacío.
  // Devuelve la lista de problemas (vacía si la fila está OK).
  const problemasFila = (p) => {
    const probs = [];
    const c = Number(p.contado);
    const cuotas = [['3 cuotas', p.cuotas3], ['6 cuotas', p.cuotas6], ['10 cuotas', p.cuotas10], ['2 cuotas', p.cuotas2]];
    // SURA cotiza el valor POR CUOTA (no el total), así que es normal y esperado
    // que cada cuota sea menor que el contado. No aplicamos esa alarma a SURA.
    const esSura = (p.company || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').includes('SURA');
    if (c > 0) {
      cuotas.forEach(([label, v]) => {
        const n = Number(v);
        // Una cuota por debajo del 90% del contado es casi seguro dato corrupto
        // (salvo SURA, que cotiza por cuota).
        if (!esSura && n > 0 && n < c * 0.9) probs.push(`${label} (${n.toLocaleString('es-UY')}) es menor que el contado`);
        // Una cuota MUY por encima del contado (más de +30%) puede ser un valor
        // de otra columna (ej. 11 cuotas) corrido de lugar.
        if (n > c * 1.3) probs.push(`${label} (${n.toLocaleString('es-UY')}) es muy superior al contado — ¿columna corrida?`);
      });
    }
    // "Banco Seguros" suele ser una mala lectura de Sancor; BSE en el sistema se
    // escribe BSE. Si aparece "Banco Seguros" como compañía, pedimos verificar.
    const co = (p.company || '').toUpperCase();
    if (co.includes('BANCO') && co.includes('SEGURO') && !co.includes('BSE')) {
      probs.push('compañía "Banco Seguros": verificá si es Sancor o BSE');
    }
    return probs;
  };
  const filasConProblema = (form.prices || []).map((p, idx) => ({ idx, p, probs: problemasFila(p) })).filter(x => x.probs.length > 0);

  // Genera el PDF de UNA cobertura. Si cob es null, incluye todas las filas (comportamiento original).
  const generatePDF = (cob = null) => {
    const filas = cob == null ? (form.prices || []) : (form.prices || []).filter(p => cobDeFila(p) === cob);
    if (filas.length === 0) {
      alert(cob == null
        ? 'La cotización no tiene ninguna fila cargada, así que el PDF saldría con la tabla vacía.\n\nCargá el PDF del gestor (o agregá las compañías a mano) y volvé a intentar.'
        : `No hay ninguna fila con cobertura "${cob}", así que el PDF saldría con la tabla vacía.`);
      return;
    }
    // Deducible faltante: en coberturas PARCIALES (RC, Hurto e Incendio) varias
    // compañías legítimamente no llevan deducible, así que NO se bloquea: se
    // avisa y se deja decidir. En esas filas el PDF muestra "A confirmar".
    const sinDed = filas.filter(p => !(Number(p.deducible) > 0));
    if (sinDed.length > 0) {
      const nombres = sinDed.map(p => p.company + (p.plan ? ` (${p.plan})` : '')).join(', ');
      const cobTxt = (cob || form.cobertura || '').toString().toLowerCase();
      const esParcial = /rc|hurto|incendio|responsabilidad|parcial/.test(cobTxt);
      const msg = esParcial
        ? `Estas compañías no tienen deducible cargado: ${nombres}.\n\nEn coberturas de RC / Hurto e Incendio es normal que algunas no lleven deducible. En el PDF figurarán como "A confirmar".\n\n¿Generar el PDF igual?`
        : `Atención: estas compañías no tienen deducible cargado: ${nombres}.\n\nEn cobertura total no es habitual un deducible vacío (suele ser un error de lectura). Revisá esas filas.\n\n¿Generar el PDF igual?`;
      if (!window.confirm(msg)) return;
    }
    // Bloqueo por precios incoherentes (cuotas menores que el contado). Es el
    // error que más daño hace: mandar al cliente precios corruptos del cotizador.
    const conProblema = filas.map(p => ({ p, probs: problemasFila(p) })).filter(x => x.probs.length > 0);
    if (conProblema.length > 0) {
      const detalle = conProblema.map(x => `• ${x.p.company}${x.p.plan ? ` (${x.p.plan})` : ''}: ${x.probs.join('; ')}`).join('\n');
      const seguir = window.confirm(
        'ATENCIÓN: hay filas con precios que parecen incorrectos (cuotas más baratas que el contado, típico error del cotizador):\n\n'
        + detalle
        + '\n\nRevisá esos valores contra el cotizador original antes de enviar al cliente.\n\n¿Generar el PDF de todos modos?'
      );
      if (!seguir) return;
    }
    const html = generateQuoteHTML({ ...form, cobertura: cob || form.cobertura, prices: filas, id: quote?.id || Date.now().toString() }, companies);
    entregarPDF(html, `Cotizacion-${(form.clientName || 'NEBB').replace(/\s+/g, '_')}${cob ? '-' + cob.replace(/\s+/g, '_') : ''}.pdf`,
      quote?.id ? { quoteId: quote.id, clave: cob || 'todas', etiqueta: cob || 'Todas las coberturas', prices: form.prices || [] } : null);
  };

  const sortedCompanies = [...companies].sort((a,b) => a.name.localeCompare(b.name));
  const sortedClients = [...(useApp().clients || [])].sort((a,b) => a.name?.localeCompare(b.name) || 0);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 860 }}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>
              {isEdit ? 'Editar cotización' : 'Nueva cotización'}
            </h2>
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              {pasos.map(s => (
                <span key={s} onClick={() => setStep(s)} style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, cursor: 'pointer',
                  background: step === s ? 'var(--accent)' : 'var(--bg-input)', color: step === s ? '#fff' : 'var(--text-muted)', fontWeight: step === s ? 700 : 400 }}>
                  {nombrePaso[s]}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          {step === 0 && <Seguimiento form={form} set={set} usuario={currentUser?.name || currentUser?.email} />}
          {/* PASO 1 — Datos del bien */}
          {step === 1 && (
            <>
              <div onClick={() => !iaLoading && pdfRef.current?.click()} style={{
                border: `2px dashed ${iaLoading ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 12, padding: 18, textAlign: 'center',
                cursor: iaLoading ? 'default' : 'pointer', background: 'var(--accent-dim)', marginBottom: 18,
              }}>
                <input ref={pdfRef} type="file" onClick={e => { e.target.value = null; }} accept=".pdf" onChange={handleGestorPdf} style={{ display: 'none' }} />
                {iaLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent)' }}>
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: 13 }}>Leyendo cotización del gestor...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent)' }}>
                    <Sparkles size={18} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Cargar PDF del gestor (Wink) con IA</span>
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>La IA completa el vehículo y los precios automáticamente. Después podés editar todo.</div>
              </div>
              {iaError && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>{iaError}</div>}

              {/* Adjuntos: los PDFs cargados quedan guardados en la cotización */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  📎 {(form.attachments || []).length} PDF{(form.attachments || []).length !== 1 ? 's' : ''} adjunto{(form.attachments || []).length !== 1 ? 's' : ''}
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => attachRef.current?.click()}>
                  Adjuntar PDF sin extraer
                </button>
                <input ref={attachRef} type="file" onClick={e => { e.target.value = null; }} accept=".pdf" onChange={handleAdjuntarSinIA} style={{ display: 'none' }} />
              </div>
              {(form.attachments || []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                  {(form.attachments || []).map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '6px 10px', background: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <FileText size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{(a.size / 1024).toFixed(0)} KB</span>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => verAdjunto(a)}>Ver</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '4px 6px' }} onClick={() => quitarAdjunto(i)}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}


              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Datos del cliente y bien a asegurar</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Cliente (nombre)</label>
                  <input value={form.clientName} onChange={e => set('clientName', e.target.value)} placeholder="Nombre del cliente" list="clients-list" />
                  <datalist id="clients-list">
                    {sortedClients.map(c => <option key={c.id} value={c.name} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input value={form.clientEmail || ''} onChange={e => set('clientEmail', e.target.value)} placeholder="email@ejemplo.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Celular</label>
                  <input value={form.clientPhone || ''} onChange={e => set('clientPhone', e.target.value)} placeholder="09X XXX XXX" />
                </div>
                <div className="form-group">
                  <label className="form-label">Marca *</label>
                  <input value={form.marca} onChange={e => set('marca', e.target.value)} required placeholder="Ej: Toyota" />
                </div>
                <div className="form-group">
                  <label className="form-label">Modelo *</label>
                  <input value={form.modelo} onChange={e => set('modelo', e.target.value)} required placeholder="Ej: Corolla" />
                </div>
                <div className="form-group">
                  <label className="form-label">Año</label>
                  <input type="number" value={form.anio} onChange={e => set('anio', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Zona de circulación</label>
                  <select value={form.zona} onChange={e => set('zona', e.target.value)}>
                    {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Cobertura</label>
                  <select value={form.cobertura} onChange={e => set('cobertura', e.target.value)}>
                    {COBERTURAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Uso</label>
                  <select value={form.uso || 'Particular'} onChange={e => set('uso', e.target.value)}>
                    <option>Particular</option><option>Comercial</option><option>Reparto</option><option>Transporte escolar</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notas al pie</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
              </div>
            </>
          )}

          {/* PASO 2 — Precios */}
          {step === 2 && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Cargá los precios de cada compañía</div>
              {iaUsada && (
                <div style={{ fontSize: 12, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                  📋 Estos precios se leyeron automáticamente del PDF. <strong>Compará cada fila con el cotizador original</strong> (compañía, contado, cuotas y deducible) antes de generar el volante. La lectura automática puede equivocarse en tablas con columnas irregulares.
                </div>
              )}
              {form.prices.some(p => (p.company || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').includes('SURA')) && (
                <div style={{ fontSize: 12, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                  ℹ️ <strong>SURA cotiza el valor por cuota.</strong> Acá lo ves tal como figura en el cotizador (por cuota), pero en el volante final cada cuota se muestra ya multiplicada (cuota × cantidad), para que sea comparable con las demás compañías. El contado de SURA no se modifica.
                </div>
              )}
              {form.prices.some(p => !(Number(p.deducible) > 0)) && (
                <div style={{ fontSize: 12, color: '#b45309', background: '#fef3e2', border: '1px solid #fcd9a8', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                  ⚠️ Hay compañías sin deducible cargado. Ningún seguro tiene deducible cero — revisá y completá el deducible de cada fila antes de generar el PDF.
                </div>
              )}
              {filasConProblema.length > 0 && (
                <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                  🚨 <strong>Revisá estas filas contra el cotizador original antes de enviar.</strong> El sistema detectó posibles errores de lectura (precios corridos de columna, cuotas incoherentes, compañía dudosa o deducible faltante):
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {filasConProblema.map(x => (
                      <li key={x.idx}>{x.p.company}{x.p.plan ? ` (${x.p.plan})` : ''}: {x.probs.join('; ')}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <select value={newCompany} onChange={e => setNewCompany(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Seleccionar compañía...</option>
                  {sortedCompanies.filter(c => !form.prices.find(p => p.company === c.name)).map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <button className="btn btn-primary btn-sm" onClick={addCompanyRow} disabled={!newCompany}><Plus size={13} /> Agregar</button>
              </div>
              {(form.prices || []).some(p => normalizeCo(p.company).includes('BSE') || normalizeCo(p.company).includes('BANCO DE SEGUROS')) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 13, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                  <strong>BSE por edad en el PDF:</strong>
                  {RANGOS_EDAD_BSE.map(r => (
                    <label key={r.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                      <input type="checkbox" checked={rangoEdadActivo(form, r)}
                        onChange={e => set('bseEdad', { ...(form.bseEdad || {}), [r.k]: e.target.checked })} />
                      {r.corto}
                    </label>
                  ))}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', width: '100%' }}>
                    {Number(form.edad) > 0
                      ? `El cliente tiene ${form.edad} años: por defecto solo se muestra el rango que le corresponde. Podés prender o apagar cada uno.`
                      : 'Destildá los rangos que no correspondan a la edad del cliente.'}
                  </span>
                </div>
              )}
              {ciasConBonif.map(cia => {
                const filas = filasDe(cia), actual = bonifDe(cia), valor = bonifPct[cia.clave] || '';
                const iguala = pctParaIgualar(cia), mejora = pctParaIgualar(cia, 5);
                const $ = (v) => '$' + Math.round(Number(v)).toLocaleString('es-UY');
                return (
                <div key={cia.clave} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, background: '#e8f6ee', border: '1px solid #b7e1c9', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                  <strong style={{ color: '#1f6b47' }}>Bonificación {cia.nombre}</strong>
                  {actual
                    ? <span>Aplicada: <b>{actual}%</b> sobre {filas.length === 1 ? 'su precio' : `sus ${filas.length} opciones`}.</span>
                    : <span style={{ color: 'var(--text-muted)' }}>¿La compañía autorizó un descuento adicional?</span>}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                    Autorizaron
                    <input value={valor} onChange={e => setBonifPct(b => ({ ...b, [cia.clave]: e.target.value }))} inputMode="decimal" placeholder={actual ? String(actual) : '10'}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); aplicarBonif(cia, valor); } }}
                      style={{ width: 60, fontSize: 13, textAlign: 'right' }} />%
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => aplicarBonif(cia, valor)} disabled={!valor}>
                      {actual ? 'Cambiar' : 'Aplicar'}
                    </button>
                    {actual && <button type="button" className="btn btn-secondary btn-sm" onClick={() => aplicarBonif(cia, 0)}>Quitar</button>}
                  </span>
                  {iguala && iguala.pct > 0 && (
                    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12 }}>
                      <span style={{ color: 'var(--text-muted)' }}>La más barata de las demás es {iguala.rival.company} a {$(iguala.rival.contado)} contado:</span>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBonifPct(b => ({ ...b, [cia.clave]: String(iguala.pct) }))}
                        title={`Con ${iguala.pct}% la opción más barata de ${cia.nombre} queda en ${$(iguala.objetivo)} o menos`}>
                        Igualar → {iguala.pct}%
                      </button>
                      {mejora && mejora.pct > 0 && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBonifPct(b => ({ ...b, [cia.clave]: String(mejora.pct) }))}
                          title={`Con ${mejora.pct}% queda 5% por debajo de ${iguala.rival.company}`}>
                          Igualar y 5% menos → {mejora.pct}%
                        </button>
                      )}
                    </div>
                  )}
                  {actual && (
                    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      <button type="button" onClick={() => avisarBonifWhatsApp(cia)} disabled={saving} className="btn btn-sm"
                        style={{ background: '#25d366', color: '#fff', border: 'none' }}>
                        Guardar y avisar al cliente por WhatsApp
                      </button>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {numeroWA(telCliente)
                          ? 'Guarda los precios nuevos y abre WhatsApp con el mensaje y el link a su cotización actualizada (lo revisás antes de mandarlo).'
                          : 'Falta el celular del cliente para avisarle por WhatsApp.'}
                      </span>
                    </div>
                  )}
                </div>
                ); })}
              {form.prices.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Seleccioná compañías para agregar sus precios</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {form.prices.map((p, idx) => {
                  const probs = problemasFila(p);
                  return (
                  <div key={idx} style={{ background: 'var(--bg-input)', borderRadius: 10, padding: '12px 14px', border: probs.length ? '1.5px solid #f87171' : '1.5px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
                      <input value={p.company} onChange={e => updatePrice(idx, 'company', e.target.value)} style={{ fontWeight: 700, fontSize: 14, flex: 1, background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)' }} />
                      {p.bonificacion ? <span title="Precio original de Wink, antes de la bonificación" style={{ fontSize: 11, color: '#1f6b47', background: '#e8f6ee', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>−{p.bonificacion}% · antes ${Number(p.sinBonif?.contado || 0).toLocaleString('es-UY')} contado</span> : null}
                      <button className="btn btn-danger btn-sm" style={{ padding: '3px 8px' }} onClick={() => removeCompany(idx)}><X size={11} /></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                      {[['Contado', 'contado'], ['2 cuotas', 'cuotas2'], ['3 cuotas', 'cuotas3'], ['6 cuotas', 'cuotas6'], ['10 cuotas', 'cuotas10'], ['Deducible', 'deducible']].map(([label, field]) => (
                        <div key={field} className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: 10 }}>{label}</label>
                          <input value={p[field]} onChange={e => updatePrice(idx, field, e.target.value)} placeholder="0" style={{ fontSize: 12 }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: 10 }}>Cobertura (si difiere)</label>
                        <input value={p.cobertura || ''} onChange={e => updatePrice(idx, 'cobertura', e.target.value)} placeholder={form.cobertura || 'ej: Todo Riesgo'} style={{ fontSize: 12 }} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: 10 }}>Plan / Nota (ej: 3x2, *limit)</label>
                        <input value={p.plan || ''} onChange={e => updatePrice(idx, 'plan', e.target.value)} placeholder="" style={{ fontSize: 12 }} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: 10 }}>Nota deducible (ej: + IVA, U$S)</label>
                        <input value={p.deducibleNota || ''} onChange={e => updatePrice(idx, 'deducibleNota', e.target.value)} placeholder="" style={{ fontSize: 12 }} />
                      </div>
                    </div>
                  </div>
                ); })}
              </div>
              {form.prices.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 14, padding: '10px 12px', background: 'var(--accent-dim)', borderRadius: 8 }}>
                  ✓ La tabla de beneficios por compañía (auxilio, cristales, accidentes, etc.) se agrega automáticamente al PDF según las compañías cargadas. No hace falta completarla.
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > pasos[0] && <button type="button" onClick={() => setStep(s => s-1)} className="btn btn-secondary">← Anterior</button>}
            {step < 2 && <button type="button" onClick={() => setStep(s => s+1)} className="btn btn-secondary">Siguiente →</button>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
            {form.prices.length > 0 && (coberturasDistintas.length > 1
              ? (
                <select value="" onChange={e => { if (e.target.value) generatePDF(e.target.value); }}
                  title="Generar el PDF de una cobertura" style={{ maxWidth: 230, cursor: 'pointer' }}>
                  <option value="">📄 Generar PDF… ({coberturasDistintas.length} coberturas)</option>
                  {coberturasDistintas.map(cob => <option key={cob} value={cob}>{cob}</option>)}
                </select>
              )
              : <button type="button" onClick={() => generatePDF()} className="btn btn-secondary"><FileText size={14} /> Generar PDF</button>
            )}
            {/* Guardar SIEMPRE disponible (en cualquier paso): deja la cotización
                en la lista para retomarla o exportarla después, sin generar PDF. */}
            <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
              {saving ? 'Guardando...' : (isEdit ? 'Guardar cambios' : '💾 Guardar cotización')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Cerrar una cotización desde la tarjeta ────────────────────
// Un click para pasarla a emitida (con la compañía) o a perdida (con el
// motivo), sin abrir el formulario. Queda en el historial.
function CierreRapido({ q, onCerrar }) {
  const [estado, setEstado] = useState('');
  const [cia, setCia] = useState('');
  const companias = [...new Set((q.prices || []).map(p => p.company).filter(Boolean))];
  const confirmar = (est, compania) => {
    onCerrar(q, est, compania);
    setEstado(''); setCia('');
  };
  if (estado === 'emitio') {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={cia} onChange={e => setCia(e.target.value)} style={{ flex: '1 1 150px', fontSize: 12.5, padding: '5px 8px' }}>
          <option value="">¿Con qué compañía?</option>
          {companias.map(c => <option key={c} value={c}>{c}</option>)}
          <option value="Otra">Otra</option>
        </select>
        <button type="button" className="btn btn-primary btn-sm" disabled={!cia} onClick={() => confirmar('emitio', cia)}>Listo</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEstado('')}>✕</button>
      </div>
    );
  }
  return (
    <select value="" onChange={e => { const v = e.target.value; if (!v) return; if (v === 'emitio') setEstado('emitio'); else confirmar(v); }}
      title="Pasarla a emitida o perdida: sale de Abiertas" style={{ fontSize: 12.5, padding: '5px 8px', width: '100%', cursor: 'pointer' }}>
      <option value="">{grupoDe(q) === 'abiertas' ? 'Cerrar cotización…' : 'Cambiar estado…'}</option>
      <option value="emitio">✓ Emitió con NEBB</option>
      <optgroup label="Perdida">
        {SEGUIMIENTO.filter(o => PERDIDOS.includes(o.id)).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </optgroup>
      {grupoDe(q) !== 'abiertas' && <option value="volver_contactar">Reabrir · volver a contactar</option>}
    </select>
  );
}

// ── Números del mes ───────────────────────────────────────────
function Numeros({ quotes, mes, setMes, onVerVencidas }) {
  const meses = [...new Set([mesDe(Date.now()), ...quotes.map(q => mesDe(ms(q.createdAt) || Date.now()))])].sort().reverse();
  const entraron = quotes.filter(q => mesDe(ms(q.createdAt)) === mes);
  const cerradasMes = quotes.filter(q => grupoDe(q) !== 'abiertas' && mesDe(fechaCierre(q)) === mes);
  const emitidas = cerradasMes.filter(q => grupoDe(q) === 'emitidas');
  const perdidas = cerradasMes.filter(q => grupoDe(q) === 'perdidas');
  const emitidasDeEntradas = entraron.filter(q => grupoDe(q) === 'emitidas').length;
  const conversion = entraron.length ? Math.round(emitidasDeEntradas / entraron.length * 100) : 0;
  const abiertas = quotes.filter(q => grupoDe(q) === 'abiertas').length;
  const vencidas = quotes.filter(esVencida).length;
  const contar = (lista, f) => Object.entries(lista.reduce((acc, q) => { const k = f(q); if (k) acc[k] = (acc[k] || 0) + 1; return acc; }, {})).sort((a, b) => b[1] - a[1]);
  const motivos = contar(perdidas, q => estadoWeb(q) ? 'Cancelada en Ventas web' : segDe(q.seguimiento).label);
  const cias = contar(emitidas, companiaEmitida);
  const ramos = contar(entraron, q => RAMO_LABEL[ramoDe(q)] || ramoDe(q));
  const web = entraron.filter(q => q.origen === 'web' || q.origen === 'bot').length;

  const caja = { background: 'var(--bg-input)', borderRadius: 10, padding: '12px 14px', minWidth: 120, flex: '1 1 120px' };
  const big = { fontSize: 24, fontWeight: 800, lineHeight: 1.1, fontFamily: 'var(--font-display)' };
  const chico = { fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 };
  const lista = (items, vacio) => items.length ? items.slice(0, 5).map(([k, n]) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '2px 0' }}><span>{k}</span><b>{n}</b></div>
  )) : <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{vacio}</div>;

  return (
    <div className="card" style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>Números de</div>
        <select value={mes} onChange={e => setMes(e.target.value)} style={{ maxWidth: 200, padding: '5px 8px', fontSize: 13 }}>
          {meses.map(m => <option key={m} value={m}>{nombreMes(m)}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={caja}><div style={big}>{entraron.length}</div><div style={chico}>cotizaciones nuevas{entraron.length ? ` · ${web} por la web` : ''}</div></div>
        <div style={caja}><div style={{ ...big, color: '#2fb67c' }}>{emitidas.length}</div><div style={chico}>emitidas en el mes</div></div>
        <div style={caja}><div style={{ ...big, color: '#2fb67c' }}>{conversion}%</div><div style={chico}>de las nuevas ya emitió</div></div>
        <div style={caja}><div style={{ ...big, color: '#e04b4b' }}>{perdidas.length}</div><div style={chico}>perdidas en el mes</div></div>
        <div style={caja}><div style={big}>{abiertas}</div><div style={chico}>abiertas hoy</div></div>
        <button type="button" onClick={onVerVencidas} disabled={!vencidas} title={`Abiertas sin movimiento hace más de ${DIAS_VENCIDA} días`}
          style={{ ...caja, border: 'none', textAlign: 'left', cursor: vencidas ? 'pointer' : 'default', fontFamily: 'inherit', color: 'inherit' }}>
          <div style={{ ...big, color: vencidas ? '#d9a23b' : 'inherit' }}>{vencidas}</div><div style={chico}>sin movimiento +{DIAS_VENCIDA} días{vencidas ? ' · ver' : ''}</div>
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <div style={caja}><div style={{ ...chico, marginTop: 0, marginBottom: 6, fontWeight: 700 }}>CON QUÉ COMPAÑÍA EMITIERON</div>{lista(cias, 'Sin emitidas en el mes')}</div>
        <div style={caja}><div style={{ ...chico, marginTop: 0, marginBottom: 6, fontWeight: 700 }}>POR QUÉ SE PERDIERON</div>{lista(motivos, 'Sin perdidas en el mes')}</div>
        <div style={caja}><div style={{ ...chico, marginTop: 0, marginBottom: 6, fontWeight: 700 }}>QUÉ COTIZARON</div>{lista(ramos, 'Sin cotizaciones en el mes')}</div>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function QuotesPage() {
  const { ceoMetrics, quotes, addQuote, updateQuote, deleteQuote: removeQuote, clients, currentUser, users, showToast } = useApp();
  // Ficha del cliente: por el vínculo que dejó el bot, o por nombre.
  const fichaDe = (q) => {
    const lista = clients || [];
    const c = (q.clientId && lista.find(x => x.id === q.clientId)) ||
      (q.clientName && lista.find(x => (x.name || '').trim().toLowerCase() === q.clientName.trim().toLowerCase()));
    return c || null;
  };
  const [modal, setModal] = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  const [grupo, setGrupo] = useState('abiertas');
  const [soloVencidas, setSoloVencidas] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [mes, setMes] = useState(() => mesDe(Date.now()));
  const [verNumeros, setVerNumeros] = useState(() => { try { return localStorage.getItem('nebb_cot_numeros') !== '0'; } catch { return true; } });
  const toggleNumeros = () => setVerNumeros(v => { try { localStorage.setItem('nebb_cot_numeros', v ? '0' : '1'); } catch {} return !v; });

  const cuenta = GRUPOS.reduce((acc, g) => ({ ...acc, [g.id]: g.id === 'todas' ? quotes.length : quotes.filter(q => grupoDe(q) === g.id).length }), {});
  const sinTildes = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const textoDe = (q) => sinTildes([q.clientName, q.telefono, q.clientPhone, q.email, q.clientEmail, q.marca, q.modelo, q.anio, q.numero, RAMO_LABEL[ramoDe(q)]].join(' '));
  const buscado = sinTildes(busqueda.trim());
  const visibles = quotes
    .filter(q => buscado ? buscado.split(/\s+/).every(w => textoDe(q).includes(w)) : (grupo === 'todas' || grupoDe(q) === grupo))
    .filter(q => !soloVencidas || esVencida(q))
    .sort((a, b) => ms(b.createdAt) - ms(a.createdAt));

  const cerrar = async (q, est, compania) => {
    const entrada = { fecha: new Date().toISOString(), canal: 'Panel', nota: est === 'emitio' && compania ? `Emitió con ${compania}` : '', estado: est, autor: (currentUser && currentUser.name) || '' };
    const cambios = {
      seguimiento: est, nueva: false,
      seguimientoLog: [entrada, ...(q.seguimientoLog || [])],
      cerradaEn: CERRADOS.includes(est) ? new Date().toISOString() : '',
      ...(est === 'emitio' ? { emitioCon: compania || '' } : {}),
    };
    try {
      await updateQuote(q.id, cambios);
      if (showToast) showToast(est === 'emitio' ? `${q.clientName || 'Cotización'}: emitida ✓` : (CERRADOS.includes(est) ? `${q.clientName || 'Cotización'}: pasó a Perdidas` : 'Reabierta'));
    } catch (e) { alert('No se pudo guardar: ' + e.message); }
  };

  const deleteQuote = (id) => {
    if (window.confirm('¿Eliminar esta cotización?')) removeQuote(id);
  };

  const generatePDF = (quote) => {
    if (!(quote.prices || []).length) {
      alert('Esta cotización no tiene ninguna fila cargada, así que el PDF saldría con la tabla vacía.\n\nEditala y cargá las compañías antes de generarlo.');
      return;
    }
    const sinDed = (quote.prices || []).filter(p => !(Number(p.deducible) > 0));
    if (sinDed.length > 0) {
      const nombres = sinDed.map(p => p.company + (p.plan ? ` (${p.plan})` : '')).join(', ');
      alert('No se puede generar el PDF: hay compañías sin deducible cargado.\n\nNingún seguro tiene deducible cero. Editá la cotización y completá el deducible de: ' + nombres);
      return;
    }
    const html = generateQuoteHTML(quote, []);
    // También guarda la copia para el link que ve el cliente (igual que el botón del editor).
    entregarPDF(html, `Cotizacion-${(quote.clientName || 'NEBB').replace(/\s+/g, '_')}.pdf`,
      quote.id ? { quoteId: quote.id, clave: 'todas', etiqueta: 'Todas las coberturas', prices: quote.prices || [] } : null);
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Cotizaciones</h1>
          <p className="page-subtitle">{cuenta.abiertas} abiertas · {quotes.length} en total</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={toggleNumeros}>{verNumeros ? 'Ocultar números' : 'Ver números'}</button>
          <button className="btn btn-primary" onClick={() => { setEditQuote(null); setModal(true); }}>
            <Plus size={15} /> Nueva cotización
          </button>
        </div>
      </div>

      {quotes.length > 0 && verNumeros && (
        <Numeros quotes={quotes} mes={mes} setMes={setMes} onVerVencidas={() => { setGrupo('abiertas'); setBusqueda(''); setSoloVencidas(true); }} />
      )}

      {quotes.length > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg-input)', borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
            {GRUPOS.map(g => {
              const activo = !buscado && grupo === g.id;
              return (
                <button key={g.id} type="button" onClick={() => { setGrupo(g.id); setSoloVencidas(false); setBusqueda(''); }}
                  style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: activo ? 700 : 500, cursor: 'pointer', border: 'none', borderRadius: 8, padding: '7px 14px',
                    background: activo ? 'var(--accent)' : 'transparent', color: activo ? '#fff' : 'var(--text-secondary)' }}>
                  {g.label} <span style={{ opacity: .75 }}>{cuenta[g.id]}</span>
                </button>
              );
            })}
          </div>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, celular, mail o auto…" style={{ flex: '1 1 240px', maxWidth: 380 }} />
          {soloVencidas && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSoloVencidas(false)}>
              Solo sin movimiento +{DIAS_VENCIDA} días ✕
            </button>
          )}
        </div>
      )}

      {quotes.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 64, color: 'var(--text-muted)' }}>
          <FileText size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Sin cotizaciones aún</p>
          <p style={{ fontSize: 13, marginBottom: 20 }}>Creá una cotización, cargá los precios de las compañías y generá un PDF profesional para enviar al cliente.</p>
          <button className="btn btn-primary" onClick={() => setModal(true)}>Crear primera cotización</button>
        </div>
      ) : visibles.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
          {buscado ? 'No hay cotizaciones que coincidan con la búsqueda.' : soloVencidas ? 'No hay cotizaciones abiertas sin movimiento.' : `No hay cotizaciones en ${GRUPOS.find(g => g.id === grupo).label.toLowerCase()}.`}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {visibles.map(q => (
            <div key={q.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  {(() => { const c = fichaDe(q); return c
                    ? <Link to={`/clients/${c.id}`} title="Abrir la ficha del cliente" style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', textDecoration: 'none', borderBottom: '1px dashed var(--text-muted)' }}>{q.clientName || c.name}</Link>
                    : <div style={{ fontWeight: 700, fontSize: 15 }}>{q.clientName || 'Sin cliente'}</div>; })()}
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {[q.marca, q.modelo, q.anio].filter(Boolean).join(' ')}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <span className="badge badge-blue">{q.cobertura}</span>
                  {(() => {
                    const ew = estadoWeb(q) || estadoEmision(q);
                    const g = grupoDe(q);
                    const es = ew
                      ? { label: `${estadoEmision(q) ? 'Emisión' : 'Ventas web'} · ${ew.replace(/-/g, ' ')}`, color: g === 'emitidas' ? '#2fb67c' : g === 'perdidas' ? '#e04b4b' : '#5b8def' }
                      : segDe(q.seguimiento);
                    return (<>
                      <span style={{ fontSize: 11, fontWeight: 700, color: es.color, border: `1px solid ${es.color}`, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{es.label}{q.seguimiento === 'emitio' && q.emitioCon ? ` · ${q.emitioCon}` : ''}</span>
                      {esVencida(q) && <span title={`Sin movimiento hace más de ${DIAS_VENCIDA} días`} style={{ fontSize: 11, fontWeight: 700, color: '#d9a23b', border: '1px solid #d9a23b', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>Sin movimiento · cerrala</span>}
                    </>);
                  })()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                <span>📍 {q.zona}</span>
                <span>🏢 {(q.prices||[]).length} compañías</span>
                <span>📅 {q.createdAt ? format(new Date(q.createdAt), 'dd/MM/yyyy') : '—'}</span>
                {q.proximoContacto && <span>🔔 {q.proximoContacto.split('-').reverse().join('/')}</span>}
                {q.seguimiento === 'quiere_emitir' && q.emisionPlan && q.emisionPlan.fecha && <span title="Fecha prevista de emisión" style={{ color: '#14b8a6', fontWeight: 700 }}>🗓 Emite {fechaCorta(q.emisionPlan.fecha)}</span>}
                {q.socio && <span title="Entró por la página de un aliado comercial: si emite, lleva comisión del aliado (se carga en /socios)" style={{ color: '#7c6cf8', fontWeight: 700 }}>🤝 {q.socioNombre || q.socio}</span>}
                {q.asignadoA && (() => { const u = (users || []).find(x => x.id === q.asignadoA); return u ? <span title="Responsable de la emisión">👤 {(u.name || u.email || '').split(' ')[0]}</span> : null; })()}
                {q.pdfLink === 'ok' && <span title="El link del cliente muestra el PDF de la cotización" style={{ color: '#2fb67c', fontWeight: 700 }}>🔗 PDF en el link</span>}
                {q.pdfLink === 'viejo' && <span title="Cambiaron los precios después de generar el PDF: el link ya no lo muestra. Generalo de nuevo." style={{ color: '#d9a23b', fontWeight: 700 }}>⚠ PDF del link desactualizado</span>}
                {(q.docsEmision || []).length > 0 && <span title="Documentos cargados">📄 {(q.docsEmision || []).length}</span>}
                {(q.attachments||[]).length > 0 && <span>📎 {(q.attachments||[]).length} PDF{(q.attachments||[]).length !== 1 ? 's' : ''}</span>}
              </div>
              {(() => {
                const c = fichaDe(q);
                const tel = q.clientPhone || q.telefono || (c && c.phone) || '';
                const mail = q.clientEmail || q.email || (c && c.email) || '';
                if (!tel && !mail) return null;
                const chip = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '4px 10px', textDecoration: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)' };
                return (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {numeroWA(tel) && <a href={linkWA(tel, mensajeSeguimiento({ ...q, clientName: q.clientName || (c && c.name) }))} target="_blank" rel="noopener noreferrer" title={`WhatsApp a ${tel} con el mensaje de seguimiento`} style={{ ...chip, background: '#25d366', borderColor: '#25d366', color: '#fff' }}>WhatsApp</a>}
                    {tel && <a href={`tel:${tel}`} title="Llamar" style={chip}>📞 {tel}</a>}
                    {mail && <a href={`mailto:${mail}`} title="Mandar mail" style={chip}>✉️ {mail}</a>}
                  </div>
                );
              })()}
              {(q.prices||[]).length > 0 && (() => {
                // La tarjeta muestra los mejores precios de la cobertura PRINCIPAL
                // (la del cartel). El problema: las filas parciales (RC/Hurto/
                // Incendio) son mucho más baratas y se colaban. En vez de matchear
                // el nombre exacto de la cobertura (frágil: la IA escribe "Todo
                // Riesgo" o "Cobertura Total" indistintamente), EXCLUIMOS las
                // coberturas claramente parciales, que es lo que no queremos ver
                // bajo el cartel de Todo Riesgo.
                const esParcial = (p) => /rc|hurto|incendio|responsabilidad|terceros|parcial/i.test((p.cobertura || ''));
                const cartelEsParcial = /rc|hurto|incendio|responsabilidad|terceros|parcial/i.test(q.cobertura || '');
                // Si el cartel es de cobertura total, excluimos las parciales.
                // Si el cartel YA es parcial, mostramos tal cual (no excluimos).
                const base = (q.prices || []).filter(p => p.contado && (cartelEsParcial || !esParcial(p)));
                const baseFinal = base.length > 0 ? base : (q.prices || []).filter(p => p.contado);
                const top = [...baseFinal].sort((a,b) => Number(a.contado)-Number(b.contado)).slice(0,3);
                return (
                <div style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontFamily: 'var(--font-display)', fontWeight: 600 }}>MEJORES PRECIOS (CONTADO){q.cobertura ? ` · ${q.cobertura}` : ''}</div>
                  {top.map((p, i) => (
                    <div key={`${p.company}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                      <span>{p.company}</span>
                      <span style={{ fontWeight: 700, color: 'var(--accent)' }}>${Number(p.contado).toLocaleString('es-UY')}</span>
                    </div>
                  ))}
                </div>
                );
              })()}
              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {estadoEmision(q)
                ? <a href="/emision" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>El estado se maneja en Emisión →</a>
                : estadoWeb(q)
                ? <a href="/hogar" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>El estado se maneja en Ventas web →</a>
                : <CierreRapido q={q} onCerrar={cerrar} />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => { setEditQuote(q); setModal(true); }}>
                  <Edit2 size={12} /> Editar
                </button>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => generatePDF(q)}>
                  <FileText size={12} /> PDF
                </button>
                <button className="btn btn-danger btn-sm" style={{ padding: '6px 10px' }} onClick={() => deleteQuote(q.id)}>
                  <Trash2 size={12} />
                </button>
              </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <QuoteModal
          quote={editQuote}
          onSaveNew={addQuote}
          onSaveEdit={updateQuote}
          onClose={() => { setModal(false); setEditQuote(null); }}
        />
      )}
    </div>
  );
}